import { useState, useEffect, useCallback, useRef } from "react";
import { useTourManager } from "../hooks/useTourManager";
import { TourEngine } from "./onboarding/TourEngine";
import { createPortal } from "react-dom";
import { useAuth } from "../hooks/useAuth";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { useToast } from "./ui/ToastModal";
import { VaultConfirmModal } from "./vault/VaultConfirmModal";
import { ReconcileModal } from "./ReconcileModal";
import {
  Plus,
  X,
  Trash2,
  FileText,
  Calendar,
  ExternalLink,
  Upload,
  CheckCircle2,
  Pencil,
  Sparkles,
  ShieldCheck,
  ShieldEllipsis,
  ShieldX,
  BrainCircuit,
  Loader2,
  TrendingUp,
  Layers,
  Lock,
  DollarSign,
  History,
  ChevronDown,
  Calculator,
  FileUp,
  Percent,
  ReceiptText,
  AlertTriangle,
  Unlock,
} from "lucide-react";
import type {
  ClaimsProject,
  ClaimsVariation,
  Database,
  Json,
} from "../types/database.types";
import { formatCurrency as formatCurrencyBase } from "../lib/calculations";
import { logError } from "../lib/logError";
import { retentionCeilingAmount, formatRetentionCeilingPercent } from "../lib/retentionCeiling";

// Claim-report figures always show 2 decimals ($x.yy) per Josh's request. The
// optional second arg is accepted for call-site compatibility but ignored.
const formatCurrency = (
  amount: number | undefined | null,
  _includeDecimals: boolean = true,
): string => formatCurrencyBase(amount, true);
import { releaseHireRowsForReport } from "../lib/claimDeletion";
import { excludeRemovedFromDashpivot } from "../lib/dashpivotRemoval";
import { calculatePaidVariationsTotal } from "../lib/variationClaimPreview";
import {
  normalizeManualClaimFinancials,
  type ManualRetentionMode,
} from "../lib/claimFinancials";
import { ClaimsInsight } from "./ClaimsInsight";
import { ClaimEditModal } from "./ClaimEditModal";
import { ScheduleCheckReportModal } from "./ScheduleCheckReportModal";
import { decideStartFailure, instructionsWindowCopy } from "../lib/scheduleCheckState";
import { PaidByBuilderModal } from "./PaidByBuilderModal";
import { ProgressPaymentPanel } from "./ProgressPaymentPanel";
import { VariationPaymentPanel } from "./VariationPaymentPanel";
import { RetentionPaymentPanel } from "./RetentionPaymentPanel";
import { PaymentHistoryDrawer } from "./PaymentHistoryDrawer";
import { useAiModel } from "../hooks/useAiModel";
import { fetchPaymentLineItems, clearAllPaymentData, type PaymentLineItem } from "../lib/paymentLineItems";
import { paymentDaysLabel } from "../lib/paymentDaysLabel";
import type { SubcategoryClaimInfo } from "../lib/paymentPanelShared.types";
import {
  deriveClaimsReportDisplayFinancials,
  getOperationalRetentionAmount,
} from "../lib/reconciliationSummary";

interface ClaimsReportRow {
  id: string;
  organization_id: string;
  project_id: string;
  claim_number: number;
  claim_month: number;
  claim_year: number;
  original_contract_sum: number | null;
  claimed_to_date: number | null;
  variations_this_month: number | null;
  variations_outstanding: number | null;
  subtotal: number | null;
  retention_pct: number | null;
  retention_amount: number | null;
  retention_release_amount: number | null;
  retention_release_gst: number | null;
  retention_release_total_inc_gst: number | null;
  claim_this_month: number | null;
  total_exc_gst: number | null;
  total_inc_gst: number | null;
  paid_by_builder: number | null;
  calculated_payment_amount: number | null;
  difference: number | null;
  days_before_due: number | null;
  date_paid: string | null;
  receipt_url: string | null;
  payment_schedule_url: string | null;
  payment_schedule_filename: string | null;
  pdf_url: string | null;
  pdf_filename: string | null;
  submission_date: string | null;
  payment_due_date: string | null;
  notes: string | null;
  is_manual_entry: boolean | null;
  created_at: string;
  created_by: string | null;
  schedule_check_status: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
  schedule_check_result: any | null;
  schedule_check_at: string | null;
  reconciled_at: string | null;
  reconciled_by: string | null;
  reconciled_by_name: string | null;
  version_count?: number;
  generation_count?: number;
  payment_version_count?: number;
  retention_received_amount: number | null;
  retention_received_notes: string | null;
  retention_received_at: string | null;
  progress_payment_pct?: number | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSONB from Supabase
  variation_payments?: any | null;
  has_unresolved_items?: boolean | null;
  // Frozen "claimed set" written at Send Report time (see smart-send-report).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSONB from Supabase
  claimed_set?: any | null;
  reconciliation_summary?: unknown;
}

type ClaimsReportInsert = Database["public"]["Tables"]["claims_reports"]["Insert"];

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
  project: ClaimsProject & { categories?: any[] };
  refreshKey?: number;
  onRetentionUpdate?: () => void;
  onVariationsChanged?: () => void;
}

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function fmtDate(d: string | null): string {
  if (!d) return "-";
  const dt = new Date(d);
  return `${dt.getDate()} ${MONTH_SHORT[dt.getMonth()]} ${String(dt.getFullYear()).slice(-2)}`;
}

function fmtPercent(value: number): string {
  return `${Number(value || 0).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}%`;
}

/** Today's date as a local YYYY-MM-DD string (for date inputs). */
function localTodayStr(): string {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
}

type ManualPdfMode = "record" | "upload";

function getFileExtension(fileName: string): string {
  const rawExt = fileName.split(".").pop()?.toLowerCase() ?? "";
  const safeExt = rawExt.replace(/[^a-z0-9]/g, "");
  return safeExt ? `.${safeExt}` : "";
}

function buildManualStoragePath(
  projectId: string,
  year: number,
  month: number,
  fileRole: "claim-source" | "receipt",
  file: File,
): string {
  const mm = String(month).padStart(2, "0");
  return `${projectId}/${year}-${mm}/manual/${fileRole}${getFileExtension(file.name)}`;
}

async function uploadManualPublicFile(
  bucket: "progress-claim-pdfs" | "claim-receipts",
  path: string,
  file: File,
): Promise<string> {
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    upsert: true,
    contentType: file.type || undefined,
  });
  if (error) throw error;

  const { data } = supabase.storage.from(bucket).getPublicUrl(path);
  return data?.publicUrl || "";
}

interface ClaimedSetVariationSnapshot {
  variation_id?: unknown;
  effective_amount?: unknown;
}

function isSyntheticClaimedSetId(id: unknown): boolean {
  return String(id ?? "").startsWith("__invoiced");
}

function claimedSetVariationAmount(frozenVariation: ClaimedSetVariationSnapshot): number {
  return Number(frozenVariation.effective_amount ?? 0);
}

function isUsableFrozenVariation(
  frozenVariation: ClaimedSetVariationSnapshot
): frozenVariation is { variation_id: string; effective_amount: number } {
  return (
    typeof frozenVariation.variation_id === "string" &&
    !isSyntheticClaimedSetId(frozenVariation.variation_id) &&
    Math.abs(claimedSetVariationAmount(frozenVariation)) > 0.01
  );
}

const DAYS_TONE_COLOR = {
  good: "var(--color-success-text)",
  warn: "var(--color-warning-text)",
  bad: "var(--color-error-text)",
} as const;

function daysBadge(
  paymentDueDate: string | null,
  datePaid: string | null,
  fallbackDays: number | null
) {
  const result = paymentDaysLabel(
    paymentDueDate,
    datePaid,
    fallbackDays,
    localTodayStr()
  );
  if (!result) return "-";

  return (
    <span
      title={result.tooltip}
      style={{
        color: DAYS_TONE_COLOR[result.tone],
        fontWeight: 700,
        fontSize: result.paid ? "0.75rem" : "0.78rem",
        whiteSpace: "nowrap",
      }}
    >
      {result.label}
    </span>
  );
}

const thStyle: React.CSSProperties = {
  padding: "10px 12px",
  fontWeight: 700,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--text-secondary)",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  background: "var(--bg-secondary)",
  zIndex: 1,
};

function clampNumber(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getErrorText(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === "object") {
    const record = err as Record<string, unknown>;
    const parts = [
      record.message,
      record.details,
      record.hint,
      record.code ? `Code: ${record.code}` : null,
    ]
      .filter((part): part is string => typeof part === "string" && part.length > 0);
    if (parts.length > 0) return parts.join(" ");
  }
  if (typeof err === "string") return err;
  return "Unknown error";
}

function getPaymentPopoverLayout(
  anchorRect: DOMRect,
  hasHistory: boolean,
  hasClearPayment: boolean
): React.CSSProperties {
  const margin = 12;
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1024;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 768;
  const width = Math.min(280, Math.max(220, viewportWidth - margin * 2));
  const estimatedHeight =
    46 +
    3 * 62 +
    58 +
    (hasHistory ? 58 : 0) +
    (hasClearPayment ? 58 : 0) +
    12;
  const availableHeight = Math.max(120, viewportHeight - margin * 2);
  const maxHeight = Math.min(estimatedHeight, availableHeight);
  const spaceBelow = viewportHeight - anchorRect.bottom - margin;
  const spaceAbove = anchorRect.top - margin;
  const preferBelow = spaceBelow >= Math.min(estimatedHeight, maxHeight) || spaceBelow >= spaceAbove;
  const rawTop = preferBelow
    ? anchorRect.bottom + 6
    : anchorRect.top - maxHeight - 6;
  const rawLeft = anchorRect.right - width;
  const maxTop = Math.max(margin, viewportHeight - maxHeight - margin);
  const maxLeft = Math.max(margin, viewportWidth - width - margin);

  return {
    top: clampNumber(rawTop, margin, maxTop),
    left: clampNumber(rawLeft, margin, maxLeft),
    width,
    maxHeight,
  };
}

export function ProgressClaimReportsTab({ project, refreshKey = 0, onRetentionUpdate, onVariationsChanged }: Props) {
  const { addToast } = useToast();
  const { user, profile } = useAuth();
  const tour = useTourManager('claims_reports');
  const [reports, setReports] = useState<ClaimsReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [retentionCeiling, setRetentionCeiling] = useState(0);
  const [accumulatedRetention, setAccumulatedRetention] = useState(0);
  const [paidByBuilderReport, setPaidByBuilderReport] = useState<ClaimsReportRow | null>(null);
  const [reportVariations, setReportVariations] = useState<ClaimsVariation[]>([]);
  const [reportSubcategoryClaims, setReportSubcategoryClaims] = useState<SubcategoryClaimInfo[]>([]);
  const [reportExistingLineItems, setReportExistingLineItems] = useState<PaymentLineItem[]>([]);
  const [paymentPopover, setPaymentPopover] = useState<{
    report: ClaimsReportRow;
    anchorRect: DOMRect;
  } | null>(null);
  // Standalone panel states
  const [progressPanelData, setProgressPanelData] = useState<{
    report: ClaimsReportRow;
    subcategoryClaims: SubcategoryClaimInfo[];
    existingLineItems: PaymentLineItem[];
  } | null>(null);
  const [variationPanelData, setVariationPanelData] = useState<{
    report: ClaimsReportRow;
    variations: ClaimsVariation[];
    existingLineItems: PaymentLineItem[];
  } | null>(null);
  const [retentionPanelData, setRetentionPanelData] = useState<{
    report: ClaimsReportRow;
    existingLineItems: PaymentLineItem[];
    retentionAvailableAmount: number;
  } | null>(null);
  const [panelRefreshKey, setPanelRefreshKey] = useState(0);
  const receiptRef = useRef<HTMLInputElement>(null);
  const [uploadingReceipt, setUploadingReceipt] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [clearPaymentConfirm, setClearPaymentConfirm] = useState<ClaimsReportRow | null>(null);
  const [paymentHistoryReport, setPaymentHistoryReport] = useState<ClaimsReportRow | null>(null);
  const [clearingPayment, setClearingPayment] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editingReport, setEditingReport] = useState<ClaimsReportRow | null>(null);
  const [analyzingSchedule, setAnalyzingSchedule] = useState<string | null>(null);
  const [viewingScheduleReport, setViewingScheduleReport] =
    useState<ClaimsReportRow | null>(null);

  // New state for the Schedule Instruction Modal
  // file is null when the window opens to run the check again on the schedule on file
  const [scheduleUploadModalState, setScheduleUploadModalState] = useState<{
    reportId: string;
    file: File | null;
  } | null>(null);
  const [scheduleUploadInstructions, setScheduleUploadInstructions] = useState("");

  const scheduleRef = useRef<HTMLInputElement>(null);
  const [scheduleUploadId, setScheduleUploadId] = useState<string | null>(null);
  const [reconcilingId, setReconcilingId] = useState<string | null>(null);
  const [reconciledDate, setReconciledDate] = useState("");
  // Pending un-reconcile confirmation (prevents accidental clearing on a stray click)
  const [unreconcileTarget, setUnreconcileTarget] = useState<{
    id: string;
    name: string;
    at: string | null;
  } | null>(null);
  const aiModel = useAiModel();

  const getRetentionAvailableForReport = useCallback(
    (targetReport: ClaimsReportRow): number => {
      const heldTotal = reports.reduce(
        (sum, report) => sum + getOperationalRetentionAmount(report),
        0,
      );
      const receivedExcludingTarget = reports.reduce(
        (sum, report) =>
          report.id === targetReport.id
            ? sum
            : sum + Number(report.retention_received_amount || 0),
        0,
      );
      return Math.round(Math.max(0, heldTotal - receivedExcludingTarget) * 100) / 100;
    },
    [reports],
  );

  // --- Thinking bubble messages ---
  const THINKING_MESSAGES = [
    "📄 Reading PDF pages...",
    "🔍 Extracting tables & figures...",
    "📊 Cross-referencing claim data...",
    "🎯 Applying your instructions...",
    "🧮 Verifying calculations...",
    "✅ Finalizing analysis...",
  ];
  const [thinkingMsgIdx, setThinkingMsgIdx] = useState(0);

  useEffect(() => {
    if (!analyzingSchedule) {
      setThinkingMsgIdx(0);
      return;
    }
    const interval = setInterval(() => {
      setThinkingMsgIdx((prev) => (prev + 1) % THINKING_MESSAGES.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [analyzingSchedule, THINKING_MESSAGES.length]);


  // Manual entry form
  const now = new Date();
  const [mMonth, setMMonth] = useState(now.getMonth() + 1);
  const [mYear, setMYear] = useState(now.getFullYear());
  const [mClaimNumber, setMClaimNumber] = useState(1);
  const [mClaimed, setMClaimed] = useState("");
  const [mVariations, setMVariations] = useState("");
  const [mVariationsOutstanding, setMVariationsOutstanding] = useState("");
  const [mThisClaim, setMThisClaim] = useState("");
  const [mSubmissionDate, setMSubmissionDate] = useState("");
  const [mPaymentDueDate, setMPaymentDueDate] = useState("");
  const [mRetentionPct, setMRetentionPct] = useState("10");
  const [mRetentionAmt, setMRetentionAmt] = useState("");
  const [mRetentionRelease, setMRetentionRelease] = useState("");
  const [manualRetentionMode, setManualRetentionMode] =
    useState<ManualRetentionMode>("percent");
  const [mPaymentExc, setMPaymentExc] = useState("");
  const [mPaymentInc, setMPaymentInc] = useState("");
  const [manualOverrideMode, setManualOverrideMode] = useState(false);
  const [showManualOverrideConfirm, setShowManualOverrideConfirm] =
    useState(false);
  const [mNotes, setMNotes] = useState("");
  const [manualPdfMode, setManualPdfMode] =
    useState<ManualPdfMode>("record");
  const [manualPdfFile, setManualPdfFile] = useState<File | null>(null);
  const [manualReceiptFile, setManualReceiptFile] = useState<File | null>(
    null,
  );
  const [saving, setSaving] = useState(false);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("claims_reports")
        .select("*")
        .eq("project_id", project.id)
        .order("claim_year", { ascending: true })
        .order("claim_month", { ascending: true });

      if (error) throw error;

      const pReports = (data as ClaimsReportRow[]) || [];
      const reportIds = pReports.map((r) => r.id);

      const vCounts: Record<string, number> = {};
      const vGenCounts: Record<string, number> = {};
      const paymentVersionCounts: Record<string, number> = {};
      if (reportIds.length > 0) {
        const { data: vData } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
          .from("claims_report_versions" as any)
          .select("report_id, version_type")
          .in("report_id", reportIds);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
        (vData || []).forEach((row: any) => {
          if (row.version_type === 'generation') {
            vGenCounts[row.report_id] = (vGenCounts[row.report_id] || 0) + 1;
          } else if (row.version_type === "payment" || row.version_type === "revert") {
            return;
          } else {
            vCounts[row.report_id] = (vCounts[row.report_id] || 0) + 1;
          }
        });

        const { data: paymentVersionData } = await supabase
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generated DB types lag the live versions table shape.
          .from("claims_report_versions" as any)
          .select("report_id, snapshot")
          .in("report_id", reportIds)
          .in("version_type", ["payment", "revert"]);

        (paymentVersionData || []).forEach((row: unknown) => {
          const version = row as {
            report_id?: string;
            snapshot?: { payment_line_items?: unknown };
          };
          if (
            version.report_id &&
            Array.isArray(version.snapshot?.payment_line_items)
          ) {
            paymentVersionCounts[version.report_id] =
              (paymentVersionCounts[version.report_id] || 0) + 1;
          }
        });
      }

      setReports(pReports.map(r => ({
        ...r,
        version_count: vCounts[r.id] || 0,
        generation_count: vGenCounts[r.id] || 0,
        payment_version_count: paymentVersionCounts[r.id] || 0,
      })));

      let originalContractSum = 0;
      if (project.categories) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
        project.categories.forEach((cat: any) => {
          if (cat.is_custom_amount_only) {
            originalContractSum += Number(cat.custom_amount || 0);
          } else {
            const subs = cat.subcategories || cat.claims_subcategories || [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
            subs.forEach((sub: any) => {
              originalContractSum += Number(sub.amount || 0);
            });
          }
        });
      }

      // Retention ceiling base depends on mode: original / revised / custom
      const mode = project.retention_ceiling_mode || (project.retention_on_revised ? 'revised' : 'original');
      let retentionBase = originalContractSum;
      if (mode === 'revised') {
        // select("*") rather than a column list: naming dashpivot_removed_at in
        // the select would couple the retention ceiling to an unapplied
        // migration, and this query's error is not surfaced.
        const { data: varData } = await supabase
          .from("claims_variations")
          .select("*")
          .eq("project_id", project.id);
        // Third of three revised-mode retention bases. The claim modal and the
        // claim engine both exclude flagged variations; this one must agree, or
        // a cross-project duplicate inflates the retention ceiling.
        // The same certified figure the claim engine uses, so the ceiling shown
        // here, in the claim modal, on the project page and on the PDF are one
        // number. It used to sum approved_amount raw, which added credits to
        // the contract instead of subtracting them.
        const approvedVarTotal = calculatePaidVariationsTotal(
          excludeRemovedFromDashpivot(varData || []),
        );
        retentionBase = originalContractSum + approvedVarTotal;
      } else if (mode === 'custom' && project.retention_custom_amount) {
        retentionBase = Number(project.retention_custom_amount);
      }
      setRetentionCeiling(retentionCeilingAmount(retentionBase, project.retention_ceiling_percent));
      setAccumulatedRetention(
        ((data as ClaimsReportRow[]) || []).reduce(
          (s: number, r: ClaimsReportRow) =>
            s + getOperationalRetentionAmount(r),
          0,
        ),
      );
    } catch (err) {
      logError("Error fetching reports:", err);
    } finally {
      setLoading(false);
    }
  }, [project.id, project.categories, project.retention_on_revised, project.retention_ceiling_mode, project.retention_custom_amount, project.retention_ceiling_percent]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports, refreshKey]);

  // Claims whose check Realtime has already ended, so a late invoke error does not repeat the toast
  const settledAnalysesRef = useRef<Set<string>>(new Set());

  // --- Supabase Realtime: subscribe for analysis result updates ---
  const subscribeToAnalysis = useCallback((reportId: string) => {
    const channel = supabase
      .channel(`analysis-${reportId}`)
      .on(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase Realtime channel type requires string literal
        "postgres_changes" as any,
        {
          event: "UPDATE",
          schema: "public",
          table: "claims_reports",
          filter: `id=eq.${reportId}`,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase Realtime payload shape is untyped
        (payload: any) => {
          const newStatus = payload.new?.schedule_check_status;
          if (newStatus && newStatus !== "analyzing") {
            settledAnalysesRef.current.add(reportId);
            setAnalyzingSchedule(null);
            const status = newStatus;
            addToast({
              type: status === "match" ? "success" : status === "partial" ? "info" : "error",
              title: status === "match"
                ? "Schedule matches claim ✓"
                : status === "partial"
                  ? "Schedule partially matches"
                  : status === "failed"
                    ? "Analysis failed"
                    : "Discrepancy detected!",
            });
            fetchReports();
            supabase.removeChannel(channel);
          }
        }
      )
      .subscribe();
    return channel;
  }, [addToast, fetchReports]);

  // --- On mount: check for in-progress analyses and re-subscribe ---
  useEffect(() => {
    if (!reports.length) return;
    const analyzingReport = reports.find((r) => r.schedule_check_status === "analyzing");
    if (analyzingReport && !analyzingSchedule) {
      setAnalyzingSchedule(analyzingReport.id);
      const channel = subscribeToAnalysis(analyzingReport.id);
      return () => { supabase.removeChannel(channel); };
    }
  }, [reports, analyzingSchedule, subscribeToAnalysis]);

  const handleManualSave = async () => {
    setSaving(true);
    let manualPayload: ClaimsReportInsert | null = null;
    try {
      const orgId = requireOrganizationId();
      const retentionMode = manualRetentionMode;
      const financials = normalizeManualClaimFinancials({
        claimedToDate: parseFloat(mClaimed) || 0,
        variationsThisMonth: parseFloat(mVariations) || 0,
        variationsOutstanding: parseFloat(mVariationsOutstanding) || 0,
        claimThisMonth: manualOverrideMode ? parseFloat(mThisClaim) || 0 : 0,
        retentionMode,
        retentionPct: parseFloat(mRetentionPct) || 0,
        retentionAmount: parseFloat(mRetentionAmt) || 0,
        retentionReleaseAmount: parseFloat(mRetentionRelease) || 0,
        totalExcGstOverride: manualOverrideMode
          ? parseFloat(mPaymentExc) || 0
          : 0,
        totalIncGstOverride: manualOverrideMode
          ? parseFloat(mPaymentInc) || 0
          : 0,
      });
      if (
        manualPdfFile &&
        manualPdfFile.type &&
        manualPdfFile.type !== "application/pdf"
      ) {
        throw new Error("Historical claim attachment must be a PDF.");
      }
      if (
        manualReceiptFile &&
        manualReceiptFile.type &&
        manualReceiptFile.type !== "application/pdf" &&
        !manualReceiptFile.type.startsWith("image/")
      ) {
        throw new Error("Receipt attachment must be a PDF or image.");
      }

      let pdfUrl: string | null = null;
      let pdfFilename: string | null = null;
      let receiptUrl: string | null = null;

      if (manualPdfMode === "upload" && manualPdfFile) {
        const path = buildManualStoragePath(
          project.id,
          mYear,
          mMonth,
          "claim-source",
          manualPdfFile,
        );
        pdfUrl = await uploadManualPublicFile(
          "progress-claim-pdfs",
          path,
          manualPdfFile,
        );
        pdfFilename = manualPdfFile.name;
      }

      if (manualReceiptFile) {
        const path = buildManualStoragePath(
          project.id,
          mYear,
          mMonth,
          "receipt",
          manualReceiptFile,
        );
        receiptUrl = await uploadManualPublicFile(
          "claim-receipts",
          path,
          manualReceiptFile,
        );
      }

      const manualProgressAmount = Math.max(
        0,
        financials.subtotal -
          financials.variationsThisMonth -
          financials.variationsOutstanding,
      );
      const claimedSet = {
        generated_at: new Date().toISOString(),
        source: "manual_log_claim",
        total_inc_gst: financials.totalIncGst,
        retention_amount: financials.retentionAmount,
        retention_pct: financials.retentionPct,
        retention_release_amount: financials.retentionReleaseAmount,
        retention_release_gst: financials.retentionReleaseGst,
        retention_release_total_inc_gst: financials.retentionReleaseTotalIncGst,
        manual_entry: {
          retention_mode: retentionMode,
          pdf_mode: manualPdfMode,
          has_pdf_attachment: Boolean(pdfUrl),
          has_receipt_attachment: Boolean(receiptUrl),
          calculated_fields_locked: !manualOverrideMode,
          override_mode: manualOverrideMode,
          overridden_fields: {
            claim_this_month: manualOverrideMode && Boolean(mThisClaim.trim()),
            total_exc_gst: manualOverrideMode && Boolean(mPaymentExc.trim()),
            total_inc_gst: manualOverrideMode && Boolean(mPaymentInc.trim()),
          },
        },
        progress:
          manualProgressAmount > 0
            ? [
                {
                  subcategory_id: null,
                  claimed_percentage: 0,
                  claimed_amount: manualProgressAmount,
                  label: "Manual claim value",
                },
              ]
            : [],
        variations: [
          {
            variation_id: "__manual_variations_this_month",
            effective_amount: financials.variationsThisMonth,
            label: "Manual variations this month",
          },
          {
            variation_id: "__manual_variations_outstanding",
            effective_amount: financials.variationsOutstanding,
            label: "Manual variations outstanding",
          },
        ].filter((item) => Math.abs(item.effective_amount) > 0.01),
      } as Json;

      manualPayload = {
        organization_id: orgId,
        project_id: project.id,
        claim_number: mClaimNumber,
        claim_month: mMonth,
        claim_year: mYear,
        original_contract_sum: financials.originalContractSum,
        claimed_to_date: financials.claimedToDate,
        variations_this_month: financials.variationsThisMonth,
        variations_outstanding: financials.variationsOutstanding,
        subtotal: financials.subtotal,
        retention_pct: financials.retentionPct,
        retention_amount: financials.retentionAmount,
        retention_release_amount: financials.retentionReleaseAmount,
        retention_release_gst: financials.retentionReleaseGst,
        retention_release_total_inc_gst: financials.retentionReleaseTotalIncGst,
        claim_this_month: financials.claimThisMonth,
        total_exc_gst: financials.totalExcGst,
        total_inc_gst: financials.totalIncGst,
        calculated_payment_amount: financials.calculatedPaymentAmount,
        paid_by_builder: financials.paidByBuilder,
        difference: financials.difference,
        claimed_set: claimedSet,
        created_by: user?.id ?? null,
        is_manual_entry: true,
        submission_date: mSubmissionDate || null,
        payment_due_date: mPaymentDueDate || null,
        notes: mNotes.trim() || null,
      };

      if (pdfUrl) {
        manualPayload.pdf_url = pdfUrl;
        manualPayload.pdf_filename = pdfFilename;
      }
      if (receiptUrl) {
        manualPayload.receipt_url = receiptUrl;
      }

      const { error } = await supabase.from("claims_reports").upsert(
        manualPayload,
        { onConflict: "project_id,claim_month,claim_year" },
      );
      if (error) throw error;
      addToast({ type: "success", title: "Claim record saved" });
      setShowManualEntry(false);
      resetForm();
      fetchReports();
    } catch (err) {
      const message = getErrorText(err);
      logError("[ClaimsReports] Manual claim save failed", err);
      addToast({
        type: "error",
        title: "Failed to save",
        message,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      // 1. Fetch the report + version PDFs BEFORE deleting
      const { data: report, error: fetchErr } = await supabase
        .from("claims_reports")
        .select("*")
        .eq("id", id)
        .single();
      if (fetchErr || !report) throw new Error("Could not find report to delete");

       
      const { data: versions } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic table not in generated Supabase types
        .from("claims_report_versions" as any)
        .select("pdf_url")
        .eq("report_id", id);

      // 2. Helper to extract storage path from a full public URL
      const extractStoragePath = (url: string, bucket: string): string | null => {
        try {
          const marker = `/storage/v1/object/public/${bucket}/`;
          const idx = url.indexOf(marker);
          if (idx === -1) return null;
          return decodeURIComponent(url.substring(idx + marker.length));
        } catch { return null; }
      };

      // 3. Collect all storage paths to delete
      const pdfPaths: string[] = [];
      const receiptPaths: string[] = [];
      const schedulePaths: string[] = [];

      // Main report PDF
      if (report.pdf_url) {
        const p = extractStoragePath(report.pdf_url, "progress-claim-pdfs");
        if (p) pdfPaths.push(p);
      }
      // Version PDFs
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (versions || []).forEach((v: any) => {
        if (v.pdf_url) {
          const p = extractStoragePath(v.pdf_url, "progress-claim-pdfs");
          if (p) pdfPaths.push(p);
        }
      });
      // Receipt
      if (report.receipt_url) {
        const p = extractStoragePath(report.receipt_url, "claim-receipts");
        if (p) receiptPaths.push(p);
      }
      // Payment schedule
      if (report.payment_schedule_url) {
        const p = extractStoragePath(report.payment_schedule_url, "payment-schedules");
        if (p) schedulePaths.push(p);
      }

      // 4. Clean up variation batch folder: {projectId}/{YYYY-MM}/variations/*
      const mm = String(report.claim_month).padStart(2, "0");
      const variationsPrefix = `${report.project_id}/${report.claim_year}-${mm}/variations`;
      const { data: varFiles } = await supabase.storage
        .from("progress-claim-pdfs")
        .list(variationsPrefix, { limit: 200 });
      if (varFiles && varFiles.length > 0) {
        const varPaths = varFiles.map((f) => `${variationsPrefix}/${f.name}`);
        pdfPaths.push(...varPaths);
      }

      // 5. Release hire billing rows FIRST: claims_hire_billing.claim_report_id
      //    is ON DELETE NO ACTION, so a hire-bearing claim is undeletable until
      //    its rows are released (they become re-claimable, matching
      //    isHireRowClaimable). Throws on failure so the delete never runs half-done.
      const hireRowsReleased = await releaseHireRowsForReport(supabase, id);

      // 6. Delete the DB row (CASCADE will remove claims_report_versions)
      const { data: deleted, error: deleteErr } = await supabase
        .from("claims_reports")
        .delete()
        .eq("id", id)
        .select("id");
      if (deleteErr) throw deleteErr;
      if (!deleted || deleted.length === 0) {
        throw new Error("Delete returned 0 rows, record may not have been removed");
      }

      // 7. Delete storage files AFTER the DB delete succeeds (best effort,
      //    the claim row is already gone, so a storage failure must not reach
      //    the catch and report "Failed to delete"). Removing them earlier
      //    left "PDFs gone but claim still listed" whenever a later step failed.
      try {
        if (pdfPaths.length > 0) {
          await supabase.storage.from("progress-claim-pdfs").remove(pdfPaths);
        }
        if (receiptPaths.length > 0) {
          await supabase.storage.from("claim-receipts").remove(receiptPaths);
        }
        if (schedulePaths.length > 0) {
          await supabase.storage.from("payment-schedules").remove(schedulePaths);
        }
      } catch (storageErr) {
        logError("Storage cleanup after claim delete failed:", storageErr);
      }

      // 8. Log to activity trail
      if (user) {
        await supabase.from("claims_activity_logs").insert({
          organization_id: requireOrganizationId(),
          project_id: report.project_id,
          user_id: user.id,
          action_type: "deleted",
          entity_type: "claims_report",
          entity_id: id,
          description: `Claim #${report.claim_number} for ${report.claim_month}/${report.claim_year} deleted by ${profile?.full_name || user.email || "Unknown"}`,
          metadata: {
            claim_month: report.claim_month,
            claim_year: report.claim_year,
            total_inc_gst: report.total_inc_gst,
            pdf_url: report.pdf_url,
            storage_files_cleaned: pdfPaths.length + receiptPaths.length + schedulePaths.length,
            hire_rows_released: hireRowsReleased,
          },
        });
      }

      addToast({ type: "success", title: "Claim record and all related files deleted" });
      setDeleteConfirm(null);
      fetchReports();
    } catch (err) {
      logError("Delete failed:", err);
      addToast({
        type: "error",
        title: "Failed to delete",
        // getErrorText unwraps PostgrestError shapes (message/details/hint/code);
        // instanceof Error alone reduced every DB failure to "Unknown error".
        message: getErrorText(err),
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenPaidByBuilder = async (report: ClaimsReportRow) => {
    try {
      // 1. Fetch variations this report is responsible for, matching Send Report's
      //    logic (by variation_date): variations raised THIS month PLUS prior-month
      //    "outstanding" variations that still have an unpaid (awaiting) amount.
      //    Including outstanding ones here closes the loophole where a variation was
      //    in the report total but could not be reconciled, and keeps the variation's
      //    waiting amount as the single source of truth (paying it drops it from
      //    every future report so it is never double-counted).
      // Deliberately NOT filtered by dashpivot_removed_at: a payment must stay
      // recordable against a variation that was already claimed and invoiced,
      // even if its Dashpivot form later vanished. The frozen claimed_set below
      // is what bounds this list.
      const { data: allProjectVariations } = await supabase
        .from("claims_variations")
        .select("*")
        .eq("project_id", report.project_id);

      const variationWaiting = (v: ClaimsVariation): number => {
        const total = Number(v.total ?? 0);
        const discount = Number(v.discount_percent ?? 0);
        const effective = discount > 0 ? total * (1 - discount / 100) : total;
        return Math.round((effective - Number(v.approved_amount ?? 0)) * 100) / 100;
      };
      const isInReportMonth = (v: ClaimsVariation): boolean => {
        const d = v.variation_date ? new Date(v.variation_date) : null;
        return (
          !!d &&
          d.getMonth() + 1 === report.claim_month &&
          d.getFullYear() === report.claim_year
        );
      };
      const isPriorOutstanding = (v: ClaimsVariation): boolean => {
        const d = v.variation_date ? new Date(v.variation_date) : null;
        if (!d) return false;
        const before =
          d.getFullYear() < report.claim_year ||
          (d.getFullYear() === report.claim_year &&
            d.getMonth() + 1 < report.claim_month);
        return before && variationWaiting(v) > 0.01;
      };
      // If the report has a frozen claimed_set, reconcile only the variations it
      // invoiced. Zero-amount rows came from an inaccurate legacy backfill after
      // some variations were already paid, so they are not payment candidates.
      const frozenVars: ClaimedSetVariationSnapshot[] | null = Array.isArray(
        report.claimed_set?.variations
      )
        ? report.claimed_set.variations
        : null;
      const realFrozenVarIds = (frozenVars ?? [])
        .filter(isUsableFrozenVariation)
        .map((fv) => fv.variation_id);
      const allVars = (allProjectVariations as ClaimsVariation[]) || [];
      let relevantVariations: ClaimsVariation[];
      if (frozenVars) {
        const byId = new Map(allVars.map((v) => [v.id, v]));
        relevantVariations = realFrozenVarIds
          .map((id) => byId.get(id))
          .filter(Boolean) as ClaimsVariation[];
      } else {
        relevantVariations = allVars.filter(
          (v) => isInReportMonth(v) || isPriorOutstanding(v),
        );
      }
      setReportVariations(relevantVariations);

      // 2. Fetch subcategory claims for per-item breakdown
      const { data: progressClaims } = await supabase
        .from("claims_progress")
        .select(`
          id,
          subcategory_id,
          percentage,
          amount,
          claims_subcategories!inner (
            id,
            name,
            amount,
            claims_categories!inner (
              name,
              project_id
            )
          )
        `)
        .eq("claim_month", report.claim_month)
        .eq("claim_year", report.claim_year)
        .eq("organization_id", requireOrganizationId())
        .eq("claims_subcategories.claims_categories.project_id", report.project_id);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const subcatClaims: SubcategoryClaimInfo[] = ((progressClaims as any[]) || []).map((pc: any) => ({
        subcategory_id: pc.subcategory_id,
        subcategory_name: pc.claims_subcategories?.name || "Unknown",
        category_name: pc.claims_subcategories?.claims_categories?.name || "Unknown",
        budget: Number(pc.claims_subcategories?.amount ?? 0),
        claimed_percentage: Number(pc.percentage ?? 0),
        claimed_amount: Number(pc.amount ?? 0),
      }));
      setReportSubcategoryClaims(subcatClaims);

      // 3. Fetch existing payment line items (if any)
      const existingItems = await fetchPaymentLineItems(report.id);
      setReportExistingLineItems(existingItems);

      setPaidByBuilderReport(report);
    } catch (err) {
      logError("Error fetching payment data:", err);
      // Open modal with what we have
      setReportVariations([]);
      setReportSubcategoryClaims([]);
      setReportExistingLineItems([]);
      setPaidByBuilderReport(report);
    }
  };

  // Show the small popover options (Progress / Variations / Retention)
  const handlePaymentCellClick = (report: ClaimsReportRow, e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPaymentPopover({ report, anchorRect: rect });
  };

  // User picked an option from the popover → open the CORRECT standalone panel
  // Clear all payment data for a report
  const handleClearPayment = async (report: ClaimsReportRow) => {
    setClearingPayment(true);
    try {
      const totalIncGst = Number(report.total_inc_gst || 0);
      const { success, error } = await clearAllPaymentData(
        report.id,
        totalIncGst,
        {
          projectId: report.project_id,
          claimMonth: report.claim_month,
          claimYear: report.claim_year,
        },
        `Cleared all payment reconciliation data for Claim #${report.claim_number}.`
      );
      if (!success) throw new Error(error);
      addToast({
        type: "success",
        title: "Payment Cleared",
        message: "All payment data has been removed for this claim.",
      });
      fetchReports();
    } catch (err) {
      logError("Error clearing payment:", err);
      addToast({
        type: "error",
        title: "Clear Failed",
        message: err instanceof Error ? err.message : "Could not clear payment",
      });
    } finally {
      setClearingPayment(false);
      setClearPaymentConfirm(null);
    }
  };

  const handlePaymentOptionSelect = async (section: "progress" | "variations" | "retention" | "all") => {
    if (!paymentPopover) return;
    const report = paymentPopover.report;
    setPaymentPopover(null);

    if (section === "all") {
      // Full Reconciliation → open the existing monolithic PaidByBuilderModal
      await handleOpenPaidByBuilder(report);
      return;
    }

    // Fetch shared data needed by panels
    try {
      const existingItems = await fetchPaymentLineItems(report.id);

      if (section === "progress") {
        // Fetch subcategory claims for this report's period
        // claims_progress links by claim_month+claim_year+organization_id (NOT report_id)
        const orgId = requireOrganizationId();

        const { data: progressClaims, error: progressError } = await supabase
          .from("claims_progress")
          .select(`
            id,
            subcategory_id,
            percentage,
            amount,
            claims_subcategories!inner (
              id,
              name,
              amount,
              claims_categories!inner (
                name,
                project_id
              )
            )
          `)
          .eq("claim_month", report.claim_month)
          .eq("claim_year", report.claim_year)
          .eq("organization_id", orgId)
          .eq("claims_subcategories.claims_categories.project_id", report.project_id);

        if (progressError) {
          logError("[ProgressPanel] Supabase error:", progressError);
        }

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const subcatClaims: SubcategoryClaimInfo[] = ((progressClaims as any[]) || []).map((pc: any) => ({
          subcategory_id: pc.subcategory_id,
          subcategory_name: pc.claims_subcategories?.name || "Unknown",
          category_name: pc.claims_subcategories?.claims_categories?.name || "Unknown",
          budget: Number(pc.claims_subcategories?.amount ?? 0),
          claimed_percentage: Number(pc.percentage ?? 0),
          claimed_amount: Number(pc.amount ?? 0),
        }));
        setProgressPanelData({ report, subcategoryClaims: subcatClaims, existingLineItems: existingItems });
      } else if (section === "variations") {
        // Fetch ALL project variations, same as Full Reconciliation
        // (handleOpenPaidByBuilder), so the claimed_set lookup can find every
        // variation ID without gaps.
        requireOrganizationId(); // Validates org context

        // Deliberately NOT filtered by dashpivot_removed_at: a payment must stay
        // recordable against a variation that was already claimed and invoiced,
        // even if its Dashpivot form later vanished. The frozen claimed_set below
        // is what bounds this list.
        const { data: allProjectVariations } = await supabase
          .from("claims_variations")
          .select("*")
          .eq("project_id", report.project_id);

        const allVars = (allProjectVariations as ClaimsVariation[]) || [];

        // Apply claimed_set filtering (same as Full Reconciliation) so the
        // standalone panel only shows variations actually invoiced in this report.
        const frozenVars: ClaimedSetVariationSnapshot[] | null = Array.isArray(
          report.claimed_set?.variations
        )
          ? report.claimed_set.variations
          : null;
        const realFrozenVars = (frozenVars ?? []).filter(isUsableFrozenVariation);

        let variationsData: ClaimsVariation[];
        if (frozenVars) {
          const byId = new Map(allVars.map((v) => [v.id, v]));
          variationsData = realFrozenVars
              .map((fv) => {
                const match = byId.get(fv.variation_id);
                if (!match) return null;
                return {
                  ...match,
                  total: claimedSetVariationAmount(fv),
                  discount_percent: 0,
                } as ClaimsVariation;
              })
              .filter((v): v is ClaimsVariation => v !== null);
        } else {
          // Fallback: same logic as Full Reconciliation, variation_date based
          const variationWaiting = (v: ClaimsVariation): number => {
            const total = Number(v.total ?? 0);
            const discount = Number(v.discount_percent ?? 0);
            const effective = discount > 0 ? total * (1 - discount / 100) : total;
            return Math.round((effective - Number(v.approved_amount ?? 0)) * 100) / 100;
          };
          const isInReportMonth = (v: ClaimsVariation): boolean => {
            const d = v.variation_date ? new Date(v.variation_date) : null;
            return (
              !!d &&
              d.getMonth() + 1 === report.claim_month &&
              d.getFullYear() === report.claim_year
            );
          };
          const isPriorOutstanding = (v: ClaimsVariation): boolean => {
            const d = v.variation_date ? new Date(v.variation_date) : null;
            if (!d) return false;
            const before =
              d.getFullYear() < report.claim_year ||
              (d.getFullYear() === report.claim_year &&
                d.getMonth() + 1 < report.claim_month);
            return before && variationWaiting(v) > 0.01;
          };
          variationsData = allVars.filter(
            (v) => isInReportMonth(v) || isPriorOutstanding(v),
          );
        }

        setVariationPanelData({ report, variations: variationsData, existingLineItems: existingItems });
      } else if (section === "retention") {
        setRetentionPanelData({
          report,
          existingLineItems: existingItems,
          retentionAvailableAmount: getRetentionAvailableForReport(report),
        });
      }
    } catch (err) {
      logError("Error fetching panel data:", err);
    }
  };

  const handleReceiptUpload = async (reportId: string, file: File) => {
    setUploadingReceipt(reportId);
    try {
      const ext = file.name.split(".").pop();
      const path = `${project.id}/${reportId}.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from("claim-receipts")
        .upload(path, file, { upsert: true });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage
        .from("claim-receipts")
        .getPublicUrl(path);
      const receiptUrl = urlData?.publicUrl || null;
      const { error: updateErr } = await supabase
        .from("claims_reports")
        .update({ receipt_url: receiptUrl })
        .eq("id", reportId);
      if (updateErr) throw updateErr;
      addToast({ type: "success", title: "Receipt uploaded" });
      fetchReports();
    } catch (err) {
      addToast({
        type: "error",
        title: "Upload failed",
        message: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setUploadingReceipt(null);
    }
  };

  // ─── Start an analysis of a schedule that is already stored ───────────────────
  // Used by the upload path and by "Run the check again" (SEQ-228). Subscribes to
  // Realtime BEFORE firing the edge function, and does NOT await the response:
  // the function saves results to the DB and Realtime tells us.
  const startScheduleAnalysis = (
    report: ClaimsReportRow,
    scheduleUrl: string | null,
    instructions: string,
  ) => {
    settledAnalysesRef.current.delete(report.id);
    const channel = subscribeToAnalysis(report.id);

    // A request that fails to start must not leave the spinner and channel behind
    const onStartFailed = (err: unknown) => {
      logError("Edge function invocation error:", err);
      const decision = decideStartFailure(err, settledAnalysesRef.current.has(report.id));
      if (decision.unsubscribe) supabase.removeChannel(channel);
      if (decision.clearSpinner) {
        setAnalyzingSchedule((current) => (current === report.id ? null : current));
      }
      if (decision.toast) {
        addToast({ type: "error", title: decision.toast.title, message: decision.toast.message });
      }
    };

    supabase.functions.invoke(
      "analyze-payment-schedule",
      {
        body: {
          reportId: report.id,
          paymentScheduleUrl: scheduleUrl,
          claimData: {
            claim_number: report.claim_number,
            claimed_to_date: report.claimed_to_date,
            claim_this_month: report.claim_this_month,
            retention_amount: report.retention_amount,
            retention_pct: report.retention_pct,
            total_exc_gst: report.total_exc_gst,
            total_inc_gst: report.total_inc_gst,
          },
          ai_model: aiModel,
          userInstructions: instructions,
        },
      },
    ).then(({ error }) => {
      if (error) onStartFailed(error);
    }).catch(onStartFailed);

    addToast({
      type: "info",
      title: "AI Analysis Started",
      message: "The AI is analysing your schedule. You can navigate away, results will be ready when you return.",
    });
  };

  // Run a failed check again against the schedule that is already uploaded.
  // The user may type guidance first (empty is allowed). It is not stored.
  const handleScheduleRetry = (report: ClaimsReportRow, instructions: string) => {
    if (!report.payment_schedule_url) return;
    setAnalyzingSchedule(report.id);
    startScheduleAnalysis(report, report.payment_schedule_url, instructions);
  };

  // ─── Schedule Upload + AI Analysis Handler (FIRE-AND-FORGET) ──────────────────
  const handleScheduleUpload = async (reportId: string, file: File, instructions: string) => {
    const report = reports.find((r) => r.id === reportId);
    if (!report) return;

    setAnalyzingSchedule(reportId);
    try {
      // 1. Upload file to Supabase Storage
      const ext = file.name.split(".").pop();
      const path = `${project.id}/${reportId}-schedule.${ext}`;
      const { error: uploadErr } = await supabase.storage
        .from("payment-schedules")
        .upload(path, file, { upsert: true });

      if (uploadErr) {
        logError("Storage upload error:", uploadErr);
        throw new Error("Failed to upload schedule file.");
      }

      const { data: urlData } = supabase.storage
        .from("payment-schedules")
        .getPublicUrl(path);
      const scheduleUrl = urlData?.publicUrl || null;

      // 2. Update the claims report record with the URL
      const { error: updateErr } = await supabase
        .from("claims_reports")
        .update({
          payment_schedule_url: scheduleUrl,
          payment_schedule_filename: file.name
        })
        .eq("id", reportId);

      if (updateErr) {
        logError("DB update error:", updateErr);
        throw new Error("Failed to save schedule URL to database.");
      }

      // 3 + 4. Subscribe, then fire the edge function (shared with the retry path)
      startScheduleAnalysis(report, scheduleUrl, instructions);

      setScheduleUploadId(null);
    } catch (err) {
      logError("Schedule upload error:", err);
      addToast({
        type: "error",
        title: "Upload failed",
        message: err instanceof Error ? err.message : "Unknown error",
      });
      setAnalyzingSchedule(null);
      setScheduleUploadId(null);
    }
  };

  const resetForm = () => {
    setMClaimed("");
    setMVariations("");
    setMVariationsOutstanding("");
    setMThisClaim("");
    setMSubmissionDate("");
    setMPaymentDueDate("");
    setMRetentionPct("10");
    setMRetentionAmt("");
    setMRetentionRelease("");
    setManualRetentionMode("percent");
    setMPaymentExc("");
    setMPaymentInc("");
    setManualOverrideMode(false);
    setShowManualOverrideConfirm(false);
    setMNotes("");
    setManualPdfMode("record");
    setManualPdfFile(null);
    setManualReceiptFile(null);
  };

  const handleReconcileSave = async (reportId: string) => {
    if (!reconciledDate) return;
    try {
      const displayName = profile?.full_name || user?.email || "Unknown";
      const { error } = await supabase
        .from("claims_reports")
        .update({
          reconciled_at: new Date(reconciledDate).toISOString(),
          reconciled_by: user?.id || null,
          reconciled_by_name: displayName,
        })
        .eq("id", reportId);
      if (error) throw error;
      addToast({ type: "success", title: "Marked as reconciled" });
      setReconcilingId(null);
      setReconciledDate("");
      fetchReports();
    } catch {
      addToast({ type: "error", title: "Failed to save reconciliation" });
    }
  };

  const handleReconcileClear = async (reportId: string) => {
    try {
      const { error } = await supabase
        .from("claims_reports")
        .update({
          reconciled_at: null,
          reconciled_by: null,
          reconciled_by_name: null,
        })
        .eq("id", reportId);
      if (error) throw error;
      addToast({ type: "info", title: "Reconciliation cleared" });
      fetchReports();
    } catch {
      addToast({ type: "error", title: "Failed to clear reconciliation" });
    }
  };

  const retentionPct =
    retentionCeiling > 0
      ? Math.min(100, (accumulatedRetention / retentionCeiling) * 100)
      : 0;
  const ceilingReached =
    retentionCeiling > 0 && accumulatedRetention >= retentionCeiling;
  const paymentPopoverHasHistory =
    Boolean(paymentPopover && (paymentPopover.report.payment_version_count || 0) > 0);
  const paymentPopoverHasClear =
    Boolean(paymentPopover && (
      deriveClaimsReportDisplayFinancials(paymentPopover.report).amountReceived > 0 ||
      paymentPopover.report.date_paid
    ));
  const paymentPopoverLayout = paymentPopover
    ? getPaymentPopoverLayout(
      paymentPopover.anchorRect,
      paymentPopoverHasHistory,
      paymentPopoverHasClear
    )
    : null;
  const manualPreview = normalizeManualClaimFinancials({
    claimedToDate: parseFloat(mClaimed) || 0,
    variationsThisMonth: parseFloat(mVariations) || 0,
    variationsOutstanding: parseFloat(mVariationsOutstanding) || 0,
    claimThisMonth: manualOverrideMode ? parseFloat(mThisClaim) || 0 : 0,
    retentionMode: manualRetentionMode,
    retentionPct: parseFloat(mRetentionPct) || 0,
    retentionAmount: parseFloat(mRetentionAmt) || 0,
    retentionReleaseAmount: parseFloat(mRetentionRelease) || 0,
    totalExcGstOverride: manualOverrideMode ? parseFloat(mPaymentExc) || 0 : 0,
    totalIncGstOverride: manualOverrideMode ? parseFloat(mPaymentInc) || 0 : 0,
  });
  const manualOverrideFields = {
    claimThisMonth: manualOverrideMode && Boolean(mThisClaim.trim()),
    totalExcGst: manualOverrideMode && Boolean(mPaymentExc.trim()),
    totalIncGst: manualOverrideMode && Boolean(mPaymentInc.trim()),
  };
  const manualSubtotalAfterRetention = Math.max(
    0,
    manualPreview.subtotal - manualPreview.retentionAmount,
  );
  const manualClaimPdfLabel =
    manualPdfFile?.name ||
    (manualPdfMode === "upload" ? "Choose a PDF" : "No PDF attached");
  const manualReceiptLabel = manualReceiptFile?.name || "Optional receipt";

  const closeManualEntry = () => {
    if (!saving) {
      setManualOverrideMode(false);
      setShowManualOverrideConfirm(false);
      setMThisClaim("");
      setMPaymentExc("");
      setMPaymentInc("");
      setShowManualEntry(false);
    }
  };

  return (
    <>
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Retention Ceiling */}
      {retentionCeiling > 0 && (
        <div
          className="card"
          style={{
            padding: "18px 22px",
            background:
              "linear-gradient(135deg, var(--bg-card) 0%, var(--bg-secondary) 100%)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <h3
              style={{
                fontSize: "0.78rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--text-secondary)",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "22px",
                  height: "22px",
                  borderRadius: "6px",
                  background: ceilingReached
                    ? "rgba(239,68,68,.12)"
                    : "rgba(59,130,246,.12)",
                }}
              >
                <FileText
                  size={12}
                  color={ceilingReached ? "#ef4444" : "#3b82f6"}
                />
              </span>
              Retention Ceiling ({formatRetentionCeilingPercent(project.retention_ceiling_percent)}%)
            </h3>
            <span data-seq-private=""
              style={{
                fontSize: "0.82rem",
                fontWeight: 700,
                color: ceilingReached ? "#ef4444" : "var(--text-primary)",
                background: ceilingReached
                  ? "rgba(239,68,68,0.08)"
                  : "var(--bg-tertiary)",
                padding: "3px 10px",
                borderRadius: "16px",
                border: `1px solid ${ceilingReached ? "rgba(239,68,68,0.2)" : "var(--border-color)"}`,
              }}
            >
              {formatCurrency(accumulatedRetention)} /{" "}
              {formatCurrency(retentionCeiling)}
              {ceilingReached && " (REACHED)"}
            </span>
          </div>
          <div
            style={{
              width: "100%",
              height: "8px",
              borderRadius: "4px",
              background: "var(--bg-secondary)",
              overflow: "hidden",
              border: "1px solid var(--border-color-light)",
            }}
          >
            <div
              style={{
                width: `${retentionPct}%`,
                height: "100%",
                borderRadius: "4px",
                background: ceilingReached
                  ? "linear-gradient(90deg,#ef4444,#dc2626)"
                  : retentionPct > 80
                    ? "linear-gradient(90deg,#f59e0b,#d97706)"
                    : "linear-gradient(90deg,#6366f1,#3b82f6)",
                transition: "width 0.5s ease",
              }}
            />
          </div>
        </div>
      )}

      {/* AI Claims Analyst */}
      {reports.length > 0 && (
        <ClaimsInsight
          projectName={project.job_address || "Unknown Project"}
          reports={reports.map((r) => ({
            ...(() => {
              const financials = deriveClaimsReportDisplayFinancials(r);
              return {
                total_inc_gst: financials.totalIncGst,
                paid_by_builder: financials.amountReceived,
                calculated_payment_amount: financials.totalIncGst,
                difference: financials.difference,
              };
            })(),
            period: `${MONTH_SHORT[r.claim_month - 1]}-${String(r.claim_year).slice(-2)}`,
            days_before_due: r.days_before_due,
            date_paid: r.date_paid,
            submission_date: r.submission_date,
            payment_due_date: r.payment_due_date,
          }))}
          retentionCeiling={retentionCeiling}
          accumulatedRetention={accumulatedRetention}
        />
      )}

      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <h3 style={{ fontSize: "1rem", fontWeight: 700 }}>Claim History</h3>
          {reports.length > 0 && (
            <span
              style={{
                fontSize: "0.68rem",
                fontWeight: 600,
                background: "var(--bg-tertiary)",
                color: "var(--text-muted)",
                padding: "2px 8px",
                borderRadius: "10px",
                border: "1px solid var(--border-color)",
              }}
            >
              {reports.length}
            </span>
          )}
        </div>
        <button
          className="btn btn-secondary"
          style={{
            fontSize: "0.78rem",
            display: "flex",
            alignItems: "center",
            gap: "5px",
          }}
          onClick={() => setShowManualEntry(true)}
        >
          <Plus size={13} /> Log Claim
        </button>
      </div>

      {showManualEntry &&
        createPortal(
          <div
            className="manual-claim-overlay"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) closeManualEntry();
            }}
          >
            <form
              className="manual-claim-modal"
              aria-labelledby="manual-claim-title"
              role="dialog"
              aria-modal="true"
              onSubmit={(event) => {
                event.preventDefault();
                void handleManualSave();
              }}
            >
              <div className="manual-claim-modal-header">
                <div className="manual-claim-title-block">
                  <span className="manual-claim-title-icon">
                    <History size={18} />
                  </span>
                  <div>
                    <h2 id="manual-claim-title">Log Historical Claim</h2>
                    <p>Capture a prior claim with retention and source files.</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="manual-claim-icon-button"
                  onClick={closeManualEntry}
                  aria-label="Close manual claim modal"
                  disabled={saving}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="manual-claim-modal-body">
                <div className="manual-claim-layout">
                  <div className="manual-claim-main">
                    <section className="manual-claim-section">
                      <div className="manual-claim-section-title">
                        <Calendar size={16} />
                        <h3>Claim Details</h3>
                      </div>
                      <div className="manual-claim-field-grid claim-meta">
                        <div className="manual-claim-field">
                          <label className="form-label">Month</label>
                          <select
                            className="form-input"
                            value={mMonth}
                            onChange={(e) => setMMonth(Number(e.target.value))}
                          >
                            {MONTH_SHORT.map((m, i) => (
                              <option key={m} value={i + 1}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="manual-claim-field">
                          <label className="form-label">Year</label>
                          <input data-seq-private
                            type="number"
                            className="form-input"
                            value={mYear}
                            onChange={(e) => setMYear(Number(e.target.value))}
                          />
                        </div>
                        <div className="manual-claim-field">
                          <label className="form-label">Claim #</label>
                          <input data-seq-private
                            type="number"
                            className="form-input"
                            value={mClaimNumber}
                            onChange={(e) =>
                              setMClaimNumber(Number(e.target.value))
                            }
                          />
                        </div>
                        <div className="manual-claim-field">
                          <label className="form-label">Submission</label>
                          <input
                            type="date"
                            className="form-input"
                            value={mSubmissionDate}
                            onChange={(e) => setMSubmissionDate(e.target.value)}
                          />
                        </div>
                        <div className="manual-claim-field">
                          <label className="form-label">Payment Due</label>
                          <input
                            type="date"
                            className="form-input"
                            value={mPaymentDueDate}
                            onChange={(e) => setMPaymentDueDate(e.target.value)}
                          />
                        </div>
                      </div>
                    </section>

                    <section className="manual-claim-section">
                      <div className="manual-claim-section-title">
                        <DollarSign size={16} />
                        <h3>Claim Values</h3>
                      </div>
                      <div className="manual-claim-field-grid values">
                        <div className="manual-claim-field">
                          <label className="form-label">
                            Contract value ($)
                          </label>
                          <input data-seq-private
                            type="number"
                            className="form-input"
                            value={mClaimed}
                            onChange={(e) => setMClaimed(e.target.value)}
                            placeholder="0.00"
                            inputMode="decimal"
                          />
                        </div>
                        <div className="manual-claim-field">
                          <label className="form-label">
                            Variations this month ($)
                          </label>
                          <input data-seq-private
                            type="number"
                            className="form-input"
                            value={mVariations}
                            onChange={(e) => setMVariations(e.target.value)}
                            placeholder="0.00"
                            inputMode="decimal"
                          />
                        </div>
                        <div className="manual-claim-field">
                          <label className="form-label">
                            Variations outstanding ($)
                          </label>
                          <input data-seq-private
                            type="number"
                            className="form-input"
                            value={mVariationsOutstanding}
                            onChange={(e) =>
                              setMVariationsOutstanding(e.target.value)
                            }
                            placeholder="0.00"
                            inputMode="decimal"
                          />
                        </div>
                        <div className="manual-claim-field">
                          <label className="form-label">This claim ($)</label>
                          {manualOverrideMode ? (
                            <input data-seq-private
                              type="number"
                              className="form-input manual-override-input"
                              value={mThisClaim}
                              onChange={(e) => setMThisClaim(e.target.value)}
                              placeholder={manualPreview.claimThisMonth.toFixed(2)}
                              inputMode="decimal"
                            />
                          ) : (
                            <div className="manual-claim-locked-input">
                              <input
                                data-seq-private=""
                                type="text"
                                className="form-input manual-calculated-input"
                                value={formatCurrency(manualPreview.claimThisMonth)}
                                readOnly
                                aria-readonly="true"
                              />
                              <span>
                                <Lock size={12} />
                                Calculated
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </section>

                    <section className="manual-claim-section">
                      <div className="manual-claim-section-title with-aside">
                        <span>
                          <Percent size={16} />
                          <h3>Retention</h3>
                        </span>
                        <strong data-seq-private="">{formatCurrency(manualPreview.retentionAmount)}</strong>
                      </div>
                      <div className="manual-claim-segmented">
                        <button
                          type="button"
                          className={manualRetentionMode === "percent" ? "active" : ""}
                          aria-pressed={manualRetentionMode === "percent"}
                          onClick={() => {
                            if (mRetentionAmt.trim()) {
                              setMRetentionPct(
                                manualPreview.retentionPct
                                  ? String(manualPreview.retentionPct)
                                  : "",
                              );
                            }
                            setManualRetentionMode("percent");
                          }}
                        >
                          Percent
                        </button>
                        <button
                          type="button"
                          className={manualRetentionMode === "amount" ? "active" : ""}
                          aria-pressed={manualRetentionMode === "amount"}
                          onClick={() => {
                            if (mRetentionPct.trim()) {
                              setMRetentionAmt(
                                manualPreview.retentionAmount
                                  ? String(manualPreview.retentionAmount)
                                  : "",
                              );
                            }
                            setManualRetentionMode("amount");
                          }}
                        >
                          Amount
                        </button>
                      </div>
                      <div className="manual-claim-field-grid retention">
                        {manualRetentionMode === "percent" ? (
                          <div className="manual-claim-field">
                            <label className="form-label">Retention %</label>
                            <input data-seq-private
                              type="number"
                              className="form-input"
                              value={mRetentionPct}
                              onChange={(e) => setMRetentionPct(e.target.value)}
                              placeholder="0"
                              inputMode="decimal"
                            />
                          </div>
                        ) : (
                          <div className="manual-claim-field">
                            <label className="form-label">Retention held ($)</label>
                            <input data-seq-private
                              type="number"
                              className="form-input"
                              value={mRetentionAmt}
                              onChange={(e) => setMRetentionAmt(e.target.value)}
                              placeholder="0.00"
                              inputMode="decimal"
                            />
                          </div>
                        )}
                        <div className="manual-claim-field">
                          <label className="form-label">Retention release ($)</label>
                          <input data-seq-private
                            type="number"
                            className="form-input"
                            value={mRetentionRelease}
                            onChange={(e) =>
                              setMRetentionRelease(e.target.value)
                            }
                            placeholder="0.00"
                            inputMode="decimal"
                          />
                        </div>
                        <div className="manual-claim-retention-readout">
                          <span>Held</span>
                          <strong data-seq-private="">
                            {formatCurrency(manualPreview.retentionAmount)}
                          </strong>
                          <small>{fmtPercent(manualPreview.retentionPct)}</small>
                        </div>
                      </div>
                    </section>

                    <section className="manual-claim-section">
                      <div className="manual-claim-section-title with-aside">
                        <span>
                          <Calculator size={16} />
                          <h3>
                            {manualOverrideMode
                              ? "Calculated Overrides"
                              : "Calculated Totals"}
                          </h3>
                        </span>
                        {manualOverrideMode ? (
                          <button
                            type="button"
                            className="manual-claim-section-action"
                            onClick={() => {
                              setManualOverrideMode(false);
                              setMThisClaim("");
                              setMPaymentExc("");
                              setMPaymentInc("");
                            }}
                          >
                            <Lock size={13} />
                            Lock
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="manual-claim-section-action"
                            onClick={() => setShowManualOverrideConfirm(true)}
                          >
                            <Unlock size={13} />
                            Override
                          </button>
                        )}
                      </div>
                      {manualOverrideMode ? (
                        <>
                          <div className="manual-claim-override-banner">
                            <AlertTriangle size={15} />
                            <span>
                              Override only when matching an old PDF or external
                              claim record.
                            </span>
                          </div>
                          <div className="manual-claim-field-grid overrides">
                            <div className="manual-claim-field">
                              <label className="form-label">
                                Total Ex GST ($)
                              </label>
                              <input data-seq-private
                                type="number"
                                className="form-input manual-override-input"
                                value={mPaymentExc}
                                onChange={(e) => setMPaymentExc(e.target.value)}
                                placeholder={manualPreview.totalExcGst.toFixed(2)}
                                inputMode="decimal"
                              />
                            </div>
                            <div className="manual-claim-field">
                              <label className="form-label">
                                Total Inc GST ($)
                              </label>
                              <input data-seq-private
                                type="number"
                                className="form-input manual-override-input"
                                value={mPaymentInc}
                                onChange={(e) => setMPaymentInc(e.target.value)}
                                placeholder={manualPreview.totalIncGst.toFixed(2)}
                                inputMode="decimal"
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="manual-claim-readout-grid">
                          <div className="manual-claim-readout">
                            <span>Total Ex GST</span>
                            <strong data-seq-private="">
                              {formatCurrency(manualPreview.totalExcGst)}
                            </strong>
                            <small>Calculated</small>
                          </div>
                          <div className="manual-claim-readout">
                            <span>Total Inc GST</span>
                            <strong data-seq-private="">
                              {formatCurrency(manualPreview.totalIncGst)}
                            </strong>
                            <small>Calculated</small>
                          </div>
                        </div>
                      )}
                    </section>

                    <section className="manual-claim-section">
                      <div className="manual-claim-section-title">
                        <FileText size={16} />
                        <h3>Documents</h3>
                      </div>
                      <div className="manual-claim-segmented">
                        <button
                          type="button"
                          className={manualPdfMode === "record" ? "active" : ""}
                          aria-pressed={manualPdfMode === "record"}
                          onClick={() => {
                            setManualPdfMode("record");
                            setManualPdfFile(null);
                          }}
                        >
                          Record only
                        </button>
                        <button
                          type="button"
                          className={manualPdfMode === "upload" ? "active" : ""}
                          aria-pressed={manualPdfMode === "upload"}
                          onClick={() => setManualPdfMode("upload")}
                        >
                          Upload PDF
                        </button>
                      </div>
                      <div className="manual-claim-upload-grid">
                        {manualPdfMode === "upload" && (
                          <label className="manual-claim-upload-target">
                            <input
                              type="file"
                              accept="application/pdf"
                              onChange={(e) =>
                                setManualPdfFile(e.target.files?.[0] ?? null)
                              }
                            />
                            <FileUp size={18} />
                            <span>{manualClaimPdfLabel}</span>
                          </label>
                        )}
                        <label className="manual-claim-upload-target">
                          <input
                            type="file"
                            accept="application/pdf,image/*"
                            onChange={(e) =>
                              setManualReceiptFile(e.target.files?.[0] ?? null)
                            }
                          />
                          <ReceiptText size={18} />
                          <span>{manualReceiptLabel}</span>
                        </label>
                      </div>
                      <div className="manual-claim-field">
                        <label className="form-label">Notes</label>
                        <textarea
                          className="form-input"
                          value={mNotes}
                          onChange={(e) => setMNotes(e.target.value)}
                          placeholder="Optional internal note"
                          rows={3}
                        />
                      </div>
                    </section>
                  </div>

                  <aside className="manual-claim-preview">
                    <div className="manual-claim-preview-header">
                      <span>Financial Preview</span>
                      <strong>
                        Claim #{mClaimNumber || 0} - {MONTH_SHORT[mMonth - 1]} {mYear}
                      </strong>
                    </div>
                    <div className="manual-claim-preview-rows">
                      <div className="manual-claim-preview-row">
                        <span>Contract value</span>
                        <strong data-seq-private="">
                          {formatCurrency(
                            Math.max(
                              0,
                              manualPreview.subtotal -
                                manualPreview.variationsThisMonth -
                                manualPreview.variationsOutstanding,
                            ),
                          )}
                        </strong>
                      </div>
                      <div className="manual-claim-preview-row">
                        <span>Variations this month</span>
                        <strong data-seq-private="">
                          {formatCurrency(manualPreview.variationsThisMonth)}
                        </strong>
                      </div>
                      <div className="manual-claim-preview-row">
                        <span>Variations outstanding</span>
                        <strong data-seq-private="">
                          {formatCurrency(manualPreview.variationsOutstanding)}
                        </strong>
                      </div>
                      <div
                        className={`manual-claim-preview-row emphasized ${
                          manualOverrideFields.claimThisMonth ? "overridden" : ""
                        }`}
                      >
                        <span>
                          Claim this month
                          {manualOverrideFields.claimThisMonth && (
                            <small className="manual-claim-override-chip">
                              Override
                            </small>
                          )}
                        </span>
                        <strong data-seq-private="">{formatCurrency(manualPreview.subtotal)}</strong>
                      </div>
                      <div className="manual-claim-preview-row debit">
                        <span>Retention held</span>
                        <strong data-seq-private="">
                          -{formatCurrency(manualPreview.retentionAmount)}
                        </strong>
                      </div>
                      <div className="manual-claim-preview-row">
                        <span>Subtotal ex retention</span>
                        <strong data-seq-private="">
                          {formatCurrency(manualSubtotalAfterRetention)}
                        </strong>
                      </div>
                      {manualPreview.retentionReleaseAmount > 0 && (
                        <div className="manual-claim-preview-row credit">
                          <span>Retention release</span>
                          <strong data-seq-private="">
                            {formatCurrency(manualPreview.retentionReleaseAmount)}
                          </strong>
                        </div>
                      )}
                      <div
                        className={`manual-claim-preview-row ${
                          manualOverrideFields.totalExcGst ? "overridden" : ""
                        }`}
                      >
                        <span>
                          Total Ex GST
                          {manualOverrideFields.totalExcGst && (
                            <small className="manual-claim-override-chip">
                              Override
                            </small>
                          )}
                        </span>
                        <strong data-seq-private="">{formatCurrency(manualPreview.totalExcGst)}</strong>
                      </div>
                      <div
                        className={`manual-claim-preview-row ${
                          manualOverrideFields.totalExcGst ? "overridden" : ""
                        }`}
                      >
                        <span>
                          GST (10%)
                          {manualOverrideFields.totalExcGst && (
                            <small className="manual-claim-override-chip">
                              Override
                            </small>
                          )}
                        </span>
                        <strong data-seq-private="">{formatCurrency(manualPreview.gstAmount)}</strong>
                      </div>
                      <div
                        className={`manual-claim-preview-row total ${
                          manualOverrideFields.totalIncGst ? "overridden" : ""
                        }`}
                      >
                        <span>
                          Total payable
                          {manualOverrideFields.totalIncGst && (
                            <small className="manual-claim-override-chip">
                              Override
                            </small>
                          )}
                        </span>
                        <strong data-seq-private="">{formatCurrency(manualPreview.totalIncGst)}</strong>
                      </div>
                    </div>
                    <div className="manual-claim-preview-meta">
                      <span>
                        PDF:{" "}
                        {manualPdfMode === "upload" && manualPdfFile
                          ? manualPdfFile.name
                          : "None"}
                      </span>
                      <span>
                        Receipt: {manualReceiptFile ? manualReceiptFile.name : "None"}
                      </span>
                    </div>
                  </aside>
                </div>
              </div>

              <div className="manual-claim-modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeManualEntry}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2
                        size={15}
                        style={{ animation: "spin 0.8s linear infinite" }}
                      />
                      Saving
                    </>
                  ) : (
                    <>
                      <CheckCircle2 size={15} />
                      Save Historical Claim
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>,
          document.body,
        )}

      {showManualOverrideConfirm &&
        createPortal(
          <div
            className="manual-override-overlay"
            role="presentation"
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                setShowManualOverrideConfirm(false);
              }
            }}
          >
            <div
              className="manual-override-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="manual-override-title"
            >
              <div className="manual-override-icon">
                <AlertTriangle size={20} />
              </div>
              <div className="manual-override-content">
                <h2 id="manual-override-title">
                  Unlock calculated values?
                </h2>
                <p>
                  Use overrides only when a historical PDF or external record
                  does not match the calculated claim totals.
                </p>
                <div className="manual-override-list">
                  <span>
                    <Lock size={13} />
                    Calculated values will stay as placeholders.
                  </span>
                  <span>
                    <Unlock size={13} />
                    Any value you type will be marked as overridden.
                  </span>
                </div>
              </div>
              <div className="manual-override-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowManualOverrideConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setManualOverrideMode(true);
                    setShowManualOverrideConfirm(false);
                  }}
                >
                  <Unlock size={15} />
                  Unlock Values
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}

      {/* Table */}
      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "var(--text-muted)",
          }}
        >
          Loading...
        </div>
      ) : reports.length === 0 ? (
        <div
          className="card"
          style={{
            padding: "48px",
            textAlign: "center",
            color: "var(--text-muted)",
          }}
        >
          <FileText size={28} style={{ margin: "0 auto 12px", opacity: 0.4 }} />
          <p style={{ fontWeight: 600 }}>No claim reports yet</p>
          <p style={{ fontSize: "0.78rem", marginTop: "4px" }}>
            Use "Log Claim" to add historical claims, or generate from the
            Claims tab.
          </p>
        </div>
      ) : (
        <div
          className="card"
          style={{ overflow: "auto", padding: 0, maxHeight: "70vh" }}
        >
          {/* Hidden receipt file input */}
          <input
            ref={receiptRef}
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              const rid = receiptRef.current?.dataset?.reportId;
              if (file && rid) handleReceiptUpload(rid, file);
              e.target.value = "";
            }}
          />
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "0.78rem",
              minWidth: "1200px",
            }}
          >
            <thead>
              {reports.length > 0 && (() => {
                const operational = reports.map(deriveClaimsReportDisplayFinancials);
                const totalClaimed = operational.reduce((sum, value) => sum + value.claimed, 0);
                const totalVariations = operational.reduce((sum, value) => sum + value.variations, 0);
                const totalThisClaim = operational.reduce((sum, value) => sum + value.thisClaim, 0);
                const totalRetention = operational.reduce((sum, value) => sum + value.retentionHeld, 0);
                const totalRetentionRelease = operational.reduce((sum, value) => sum + value.retentionRelease, 0);
                const totalExcGST = operational.reduce((sum, value) => sum + value.totalExcGst, 0);
                const totalIncGST = operational.reduce((sum, value) => sum + value.totalIncGst, 0);
                const totalCalculatedPayment = totalIncGST;
                const totalPaid = operational.reduce((sum, value) => sum + value.amountReceived, 0);
                const totalDifference = operational.reduce((sum, value) => sum + value.difference, 0);
                return (
                  <tr style={{ backgroundColor: "var(--bg-secondary)", borderBottom: "2px solid var(--border-color)" }}>
                    <th style={{ ...thStyle, textAlign: "left", color: "var(--text-primary)" }}>Totals</th>
                    <th data-seq-private="" style={{ ...thStyle, textAlign: "right", color: "var(--text-primary)" }}>{formatCurrency(totalClaimed)}</th>
                    <th data-seq-private="" style={{ ...thStyle, textAlign: "right", color: "var(--text-primary)" }}>{formatCurrency(totalVariations)}</th>
                    <th data-seq-private="" style={{ ...thStyle, textAlign: "right", color: "var(--text-primary)" }}>{formatCurrency(totalThisClaim)}</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>
                      <div data-seq-private=""
                        style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}
                        title={[
                          totalRetention > 0 ? `Retention held: ${formatCurrency(totalRetention)}` : null,
                          totalRetentionRelease > 0 ? `Retention release: ${formatCurrency(totalRetentionRelease)}` : null,
                        ].filter(Boolean).join("\n")}
                      >
                        {totalRetention > 0 ? (
                          <span data-seq-private=""
                            aria-label={`Retention held ${formatCurrency(totalRetention)}`}
                            style={{ color: "#dc2626", fontWeight: 700 }}
                          >
                            {formatCurrency(-Math.abs(totalRetention))}
                          </span>
                        ) : (
                          <span data-seq-private="" style={{ color: "var(--text-primary)" }}>{formatCurrency(0)}</span>
                        )}
                        {totalRetentionRelease > 0 && (
                          <span data-seq-private=""
                            aria-label={`Retention release ${formatCurrency(totalRetentionRelease)}`}
                            style={{ fontSize: "0.65rem", color: "#16a34a", fontWeight: 700 }}
                          >
                            +{formatCurrency(totalRetentionRelease)}
                          </span>
                        )}
                      </div>
                    </th>
                    <th data-seq-private="" style={{ ...thStyle, textAlign: "right", color: "var(--text-primary)" }}>{formatCurrency(totalExcGST)}</th>
                    <th data-seq-private="" style={{ ...thStyle, textAlign: "right", color: "var(--text-primary)" }}>{formatCurrency(totalIncGST)}</th>
                    <th style={{ ...thStyle, textAlign: "center" }}></th>
                    <th style={{ ...thStyle, textAlign: "center" }}></th>
                    <th data-seq-private="" style={{ ...thStyle, textAlign: "right", color: "var(--text-primary)" }}>
                      {formatCurrency(totalPaid)}
                      {totalCalculatedPayment > 0 && (
                        <div data-seq-private="" style={{ fontSize: "0.65rem", color: "var(--text-muted)", fontWeight: 500, marginTop: 2 }}>
                          exp {formatCurrency(totalCalculatedPayment)}
                        </div>
                      )}
                    </th>
                    <th data-seq-private="" style={{ ...thStyle, textAlign: "right", color: "var(--text-primary)" }}>{formatCurrency(totalDifference)}</th>
                    <th colSpan={8} style={{ ...thStyle, textAlign: "center" }}></th>
                  </tr>
                );
              })()}
              <tr>
                <th style={{ ...thStyle, textAlign: "left" }}>Period</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Claimed</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Variations</th>
                <th style={{ ...thStyle, textAlign: "right" }}>This Claim</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Retention</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Exc GST</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Inc GST</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Submitted</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Due</th>
                <th style={{ ...thStyle, textAlign: "right" }} data-tour-id="cr-paid-builder-header">
                  Paid by Builder
                </th>
                <th style={{ ...thStyle, textAlign: "right" }}>Difference</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Days</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Date Paid</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Reconciled</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Receipt</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Schedule</th>
                <th style={{ ...thStyle, textAlign: "center" }}>PDF</th>
                <th
                  style={{ ...thStyle, textAlign: "center", width: "40px" }}
                ></th>
                <th
                  style={{ ...thStyle, textAlign: "center", width: "40px" }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r, idx) => {
                const financials = deriveClaimsReportDisplayFinancials(r);
                const diff = financials.difference;
                const expectedPayment = financials.totalIncGst;
                return (
                  <tr
                    key={r.id}
                    style={{
                      borderBottom: "1px solid var(--border-color-light)",
                      background:
                        idx % 2 === 0 ? "transparent" : "var(--bg-secondary)",
                    }}
                  >
                    {/* Period */}
                    <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ fontWeight: 700, fontSize: "0.82rem" }}>
                            {MONTH_SHORT[r.claim_month - 1]}-
                            {String(r.claim_year).slice(-2)}
                          </span>
                          {r.is_manual_entry && (
                            <span
                              style={{
                                fontSize: "0.55rem",
                                fontWeight: 600,
                                background: "var(--bg-tertiary)",
                                color: "var(--text-muted)",
                                padding: "1px 5px",
                                borderRadius: "3px",
                                textTransform: "uppercase",
                              }}
                              title="Manually entered record"
                            >
                              man
                            </span>
                          )}
                        </div>
                        {/* Compact badge row: dots with labels */}
                        {(Number(r.version_count || 0) > 0 || Number(r.generation_count || 0) > 0) && (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 1 }}>
                            {Number(r.version_count || 0) > 0 ? (
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  fontSize: "0.55rem",
                                  fontWeight: 600,
                                  color: "#3b82f6",
                                  cursor: "help",
                                }}
                                title={`Edited across ${r.version_count ?? 0} version${(r.version_count ?? 0) > 1 ? 's' : ''}`}
                              >
                                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#3b82f6", display: "inline-block", flexShrink: 0 }} />
                                edited
                              </span>
                            ) : null}
                            {Number(r.generation_count || 0) > 0 ? (
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 3,
                                  fontSize: "0.55rem",
                                  fontWeight: 600,
                                  color: "#a855f7",
                                  cursor: "help",
                                }}
                                title={`Regenerated ${r.generation_count ?? 0} time${(r.generation_count ?? 0) > 1 ? 's' : ''}`}
                              >
                                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#a855f7", display: "inline-block", flexShrink: 0 }} />
                                regen
                              </span>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </td>
                    <td data-seq-private="" style={{ padding: "8px 12px", textAlign: "right" }}>
                      {financials.claimed
                        ? formatCurrency(financials.claimed, true)
                        : "-"}
                    </td>
                    {/* Variations (combined: this month + outstanding) */}
                    {(() => {
                      const thisMonth = Number(r.variations_this_month || 0);
                      const outstanding = Number(r.variations_outstanding || 0);
                      const combined = financials.variations;
                      const hasValue = combined !== 0;
                      const tooltipLines = financials.source === "reconciliation"
                        ? `Reconciled variations: ${formatCurrency(combined, true)}`
                        : [
                            `This Month: ${thisMonth !== 0 ? formatCurrency(thisMonth, true) : "-"}`,
                            `Outstanding: ${outstanding !== 0 ? formatCurrency(outstanding, true) : "-"}`,
                          ].join("\n");
                      return (
                        <td data-seq-private=""
                          style={{
                            padding: "8px 12px",
                            textAlign: "right",
                            color: hasValue ? "#16a34a" : undefined,
                            fontWeight: hasValue ? 600 : undefined,
                            cursor: hasValue ? "help" : undefined,
                            position: "relative",
                          }}
                          title={hasValue ? tooltipLines : undefined}
                        >
                          {hasValue ? formatCurrency(combined, true) : "-"}
                          {financials.source === "claim" && hasValue && (thisMonth !== 0 && outstanding !== 0) && (
                            <div data-seq-private=""
                              style={{
                                fontSize: "0.6rem",
                                fontWeight: 400,
                                color: "var(--text-muted)",
                                marginTop: "1px",
                                lineHeight: 1.2,
                              }}
                            >
                              {formatCurrency(thisMonth, true)} + {formatCurrency(outstanding, true)}
                            </div>
                          )}
                        </td>
                      );
                    })()}
                    {/* This Claim */}
                    <td data-seq-private="" style={{ padding: "8px 12px", textAlign: "right" }}>
                      {financials.thisClaim
                        ? formatCurrency(financials.thisClaim, true)
                        : "-"}
                    </td>
                    {/* Retention */}
                    <td style={{ padding: "8px 12px", textAlign: "right" }}>
                      {(() => {
                        const held = financials.retentionHeld;
                        const release = financials.retentionRelease;
                        const received = financials.source === "reconciliation"
                          ? financials.retentionRelease
                          : Number(r.retention_received_amount || 0);
                        const greenRelease = financials.retentionRelease;
                        const hasRetentionActivity = Math.abs(held) > 0.005 || greenRelease > 0.005;
                        if (!hasRetentionActivity) return "-";
                        return (
                          <div data-seq-private=""
                            style={{
                              display: "inline-flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 2,
                              lineHeight: 1.15,
                            }}
                            title={[
                              Math.abs(held) > 0.005 ? `Retention held: ${formatCurrency(held)}` : null,
                              release > 0.005 ? `Retention release claimed: ${formatCurrency(release)}` : null,
                              received > 0.005 ? `Retention release received: ${formatCurrency(received)}` : null,
                            ].filter(Boolean).join("\n")}
                          >
                            {Math.abs(held) > 0.005 && (
                              <span data-seq-private=""
                                aria-label={`Retention held ${formatCurrency(held, true)}`}
                                style={{ color: "#dc2626", fontWeight: 700 }}
                              >
                                {formatCurrency(-Math.abs(held), true)}
                              </span>
                            )}
                            {greenRelease > 0.005 && (
                              <span data-seq-private=""
                                aria-label={`Retention release ${formatCurrency(greenRelease, true)}`}
                                style={{ color: "#16a34a", fontWeight: 700 }}
                              >
                                +{formatCurrency(greenRelease, true)}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    {/* Exc GST */}
                    <td data-seq-private="" style={{ padding: "8px 12px", textAlign: "right" }}>
                      {financials.totalExcGst
                        ? formatCurrency(financials.totalExcGst, true)
                        : "-"}
                    </td>
                    {/* Inc GST */}
                    <td data-seq-private=""
                      style={{
                        padding: "8px 12px",
                        textAlign: "right",
                        fontWeight: 700,
                      }}
                    >
                      {financials.totalIncGst
                        ? formatCurrency(financials.totalIncGst, true)
                        : "-"}
                    </td>
                    {/* Submitted */}
                    <td
                      style={{
                        padding: "8px 12px",
                        textAlign: "center",
                        fontSize: "0.75rem",
                      }}
                    >
                      {fmtDate(r.submission_date)}
                    </td>
                    {/* Due */}
                    <td
                      style={{
                        padding: "8px 12px",
                        textAlign: "center",
                        fontSize: "0.75rem",
                      }}
                    >
                      {fmtDate(r.payment_due_date)}
                    </td>
                    {/* Paid by Builder */}
                    <td style={{ padding: "6px 10px", textAlign: "right" }}>
                      <div
                        className={`paid-builder-cell ${financials.amountReceived > 0 ? "paid-builder-cell--has-value" : "paid-builder-cell--empty"}`}
                        {...(idx === 0 ? { 'data-tour-id': 'cr-paid-builder-cell' } : {})}
                        onClick={(e) => handlePaymentCellClick(r, e)}
                        title={financials.amountReceived > 0 ? "Click to manage payment" : "Click to add payment"}
                        role="button"
                        aria-label={`${financials.amountReceived > 0 ? "Manage" : "Add"} payment for Claim #${r.claim_number}`}
                        aria-haspopup="menu"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setPaymentPopover({ report: r, anchorRect: rect });
                          }
                        }}
                      >
                        {financials.amountReceived > 0 ? (
                          <>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                              <span data-seq-private="" style={{ fontWeight: 800, fontSize: "0.82rem", color: "#0f172a", letterSpacing: "0.01em" }}>
                                {formatCurrency(financials.amountReceived, true)}
                              </span>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 2,
                                  color: "#2563eb",
                                  fontSize: "0.6rem",
                                  fontWeight: 800,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.04em",
                                }}
                              >
                                Manage
                                <ChevronDown size={11} strokeWidth={2.5} />
                              </span>
                            </div>
                            {expectedPayment > 0 && (
                              <div data-seq-private="" style={{ fontSize: "0.58rem", color: "#64748b", marginTop: 1, fontWeight: 600 }}>
                                expected {formatCurrency(expectedPayment, true)}
                              </div>
                            )}
                          </>
                        ) : (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              background: "var(--color-primary)",
                              color: "#fff",
                              fontSize: "0.68rem",
                              fontWeight: 800,
                              padding: "5px 12px",
                              borderRadius: 6,
                              letterSpacing: "0.02em",
                              boxShadow: "0 1px 3px rgba(30,115,190,0.25)",
                              transition: "background 0.15s, box-shadow 0.15s",
                            }}
                          >
                            Add Payment
                            <ChevronDown size={12} strokeWidth={2.5} />
                          </span>
	                        )}
	                        {/* Unresolved items indicator */}
	                        {r.has_unresolved_items && financials.amountReceived > 0 && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                              marginTop: 3,
                              padding: "2px 6px",
                              background: "#fffbeb",
                              borderRadius: 4,
                              border: "1px solid #fde68a",
                            }}
                            title="This claim has unresolved payment items (voided, agreement, or works not completed)"
                          >
                            <span
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: "#f59e0b",
                                display: "inline-block",
                                animation: "pulse 2s infinite",
                              }}
                            />
                            <span
                              style={{
                                fontSize: "0.58rem",
                                fontWeight: 600,
                                color: "#d97706",
                                textTransform: "uppercase",
                                letterSpacing: "0.03em",
                              }}
                            >
                              Action needed
                            </span>
	                      </div>
                        )}
	                      </div>
                        {(r.payment_version_count || 0) > 0 && (
                          <button
                            type="button"
                            aria-label={`View payment history for Claim #${r.claim_number}`}
                            onClick={() => setPaymentHistoryReport(r)}
                            style={{
                              marginTop: 4,
                              marginLeft: "auto",
                              padding: "2px 6px",
                              borderRadius: 5,
                              border: "1px solid #e2e8f0",
                              background: "#f8fafc",
                              color: "#475569",
                              fontSize: "0.62rem",
                              fontWeight: 700,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 4,
                              cursor: "pointer",
                            }}
                            title="View payment history"
                          >
                            <History size={10} />
                            History
                          </button>
                        )}
	                    </td>
                    {/* Difference */}
                    <td
                      style={{
                        padding: "8px 12px",
                        textAlign: "right",
                        color:
                          diff > 0
                            ? "#ef4444"
                            : diff < 0
                              ? "#16a34a"
                              : undefined,
                        fontWeight: diff !== 0 ? 600 : undefined,
                      }}
                    >
                      {diff !== 0
                        ? `${formatCurrency(Math.abs(diff), true)} ${diff > 0 ? "under" : "over"}`
                        : "-"}
                    </td>
                    {/* Days */}
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      {daysBadge(r.payment_due_date, r.date_paid, r.days_before_due)}
                    </td>
                    {/* Date Paid */}
                    <td
                      style={{
                        padding: "8px 12px",
                        textAlign: "center",
                        fontSize: "0.75rem",
                      }}
                    >
                      {fmtDate(r.date_paid)}
                    </td>
                    {/* Reconciled */}
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      {r.reconciled_at ? (
                        <span
                          onClick={() =>
                            setUnreconcileTarget({
                              id: r.id,
                              name: r.reconciled_by_name || "Unknown",
                              at: r.reconciled_at,
                            })
                          }
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                            cursor: "pointer",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            background: "rgba(16,163,74,0.08)",
                            color: "#16a34a",
                            fontWeight: 600,
                            fontSize: "0.72rem",
                            border: "1px solid rgba(16,163,74,0.15)",
                          }}
                          title={`Reconciled by ${r.reconciled_by_name || "Unknown"}. Click to un-reconcile`}
                        >
                          <CheckCircle2 size={12} />
                          {fmtDate(r.reconciled_at)}
                        </span>
                      ) : (
                        <span
                          onClick={() => {
                            setReconciledDate(localTodayStr());
                            setReconcilingId(r.id);
                          }}
                          style={{
                            cursor: "pointer",
                            padding: "2px 8px",
                            borderRadius: "4px",
                            border: "1px dashed var(--border-color)",
                            display: "inline-block",
                            minWidth: "50px",
                            color: "var(--text-muted)",
                            fontSize: "0.72rem",
                          }}
                          title="Click to mark as reconciled"
                        >
                          Mark
                        </span>
                      )}
                    </td>
                    {/* Receipt */}
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      {r.receipt_url ? (
                        <a
                          href={r.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "28px",
                            height: "28px",
                            borderRadius: "6px",
                            background: "rgba(16,163,74,0.08)",
                            color: "#16a34a",
                            border: "1px solid rgba(16,163,74,0.15)",
                            textDecoration: "none",
                          }}
                          title="View receipt"
                        >
                          <FileText size={13} />
                        </a>
                      ) : (
                        <button
                          onClick={() => {
                            if (receiptRef.current) {
                              receiptRef.current.dataset.reportId = r.id;
                              receiptRef.current.click();
                            }
                          }}
                          disabled={uploadingReceipt === r.id}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "28px",
                            height: "28px",
                            borderRadius: "6px",
                            background: "var(--bg-tertiary)",
                            color: "var(--text-muted)",
                            border: "1px solid var(--border-color-light)",
                            cursor: "pointer",
                            opacity: uploadingReceipt === r.id ? 0.5 : 1,
                          }}
                          title="Upload receipt"
                        >
                          <Upload size={12} />
                        </button>
                      )}
                    </td>
                    {/* Schedule Check */}
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      {analyzingSchedule === r.id ? (
                        <div
                          style={{
                            position: "relative",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {/* Pulsing indicator */}
                          <div
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "8px",
                              background: "linear-gradient(135deg, rgba(139,92,246,0.12), rgba(124,58,237,0.08))",
                              border: "1px solid rgba(139,92,246,0.25)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              animation: "pulseGlow 2s ease-in-out infinite",
                            }}
                          >
                            <BrainCircuit size={14} color="#8b5cf6" />
                          </div>
                          {/* Glassmorphism thinking bubble */}
                          <div
                            style={{
                              position: "absolute",
                              left: "calc(100% + 10px)",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "rgba(255,255,255,0.85)",
                              backdropFilter: "blur(12px)",
                              WebkitBackdropFilter: "blur(12px)",
                              border: "1px solid rgba(139,92,246,0.15)",
                              borderRadius: "10px",
                              padding: "7px 12px",
                              whiteSpace: "nowrap",
                              fontSize: "0.7rem",
                              fontWeight: 500,
                              color: "#6d28d9",
                              boxShadow: "0 4px 16px rgba(139,92,246,0.08), 0 1px 3px rgba(0,0,0,0.04)",
                              animation: "fadeInThink 0.4s ease",
                              zIndex: 10,
                            }}
                            key={thinkingMsgIdx}
                          >
                            <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                              <span
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  background: "#8b5cf6",
                                  animation: "pulseGlow 1.5s ease-in-out infinite",
                                  flexShrink: 0,
                                }}
                              />
                              {THINKING_MESSAGES[thinkingMsgIdx]}
                            </span>
                          </div>
                        </div>
                      ) : r.schedule_check_status ? (
                        (() => {
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                          const resultObj = r.schedule_check_result as any;
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                          const unresolvedDiscrepancies = resultObj?.line_items?.filter((item: any) => item.status !== "match" && !item.acknowledged).length || 0;

                          let effectiveStatus = r.schedule_check_status;
                          // A failed check has no line items to acknowledge, so it must
                          // not fall through to "match_resolved" and show as passed.
                          // Likewise a check still marked "analyzing" that is not the one
                          // being watched has produced nothing to resolve.
                          const checkFailed = effectiveStatus === "failed";
                          const checkPending = effectiveStatus === "analyzing";
                          // Shape and text carry the state, not colour alone.
                          const checkLabel = checkFailed
                            ? "Check failed, click to view and try again"
                            : checkPending
                              ? "Schedule check has not finished"
                              : "View schedule check report";
                          if (effectiveStatus && effectiveStatus !== "match" && !checkFailed && !checkPending && unresolvedDiscrepancies === 0) {
                            effectiveStatus = "match_resolved";
                          }

                          return (
                            <div style={{ position: "relative", display: "inline-block" }}>
                              <button
                                onClick={() => setViewingScheduleReport(r)}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "28px",
                                  height: "28px",
                                  borderRadius: "6px",
                                  background:
                                    effectiveStatus === "match" || effectiveStatus === "match_resolved"
                                      ? "rgba(22,163,74,0.08)"
                                      : effectiveStatus === "partial"
                                        ? "rgba(245,158,11,0.08)"
                                        : effectiveStatus === "discrepancy" || checkFailed
                                          ? "rgba(239,68,68,0.08)"
                                          : "rgba(139,92,246,0.08)",
                                  color:
                                    effectiveStatus === "match" || effectiveStatus === "match_resolved"
                                      ? "#16a34a"
                                      : effectiveStatus === "partial"
                                        ? "#f59e0b"
                                        : effectiveStatus === "discrepancy" || checkFailed
                                          ? "#ef4444"
                                          : "#8b5cf6",
                                  border: `1px solid ${effectiveStatus === "match" || effectiveStatus === "match_resolved"
                                    ? "rgba(22,163,74,0.15)"
                                    : effectiveStatus === "partial"
                                      ? "rgba(245,158,11,0.15)"
                                      : effectiveStatus === "discrepancy" || checkFailed
                                        ? "rgba(239,68,68,0.15)"
                                        : "rgba(139,92,246,0.15)"
                                    }`,
                                  cursor: "pointer",
                                }}
                                title={checkLabel}
                                aria-label={checkLabel}
                              >
                                {checkFailed ? (
                                  <ShieldX size={13} />
                                ) : checkPending ? (
                                  <ShieldEllipsis size={13} />
                                ) : (
                                  <ShieldCheck size={13} />
                                )}
                              </button>
                              {unresolvedDiscrepancies > 0 && !checkFailed && (
                                <div style={{
                                  position: "absolute", top: "-5px", right: "-5px",
                                  background: "#ef4444", color: "white",
                                  fontSize: "9px", fontWeight: "bold",
                                  width: "14px", height: "14px", borderRadius: "50%",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  boxShadow: "0 0 0 2px var(--bg-primary)"
                                }}>
                                  {unresolvedDiscrepancies}
                                </div>
                              )}
                            </div>
                          );
                        })()
                      ) : (
                        <button
                          onClick={() => {
                            setScheduleUploadId(r.id);
                            scheduleRef.current?.click();
                          }}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "28px",
                            height: "28px",
                            borderRadius: "6px",
                            background: "transparent",
                            color: "var(--text-muted)",
                            border: "1px dashed var(--border-color)",
                            cursor: "pointer",
                            opacity: 0.5,
                          }}
                          title="Upload payment schedule for AI check"
                          onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = "1";
                            e.currentTarget.style.borderColor =
                              "rgba(139,92,246,0.4)";
                            e.currentTarget.style.color = "#8b5cf6";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = "0.5";
                            e.currentTarget.style.borderColor =
                              "var(--border-color)";
                            e.currentTarget.style.color = "var(--text-muted)";
                          }}
                        >
                          <ShieldCheck size={12} />
                        </button>
                      )}
                    </td>
                    {/* PDF */}
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      {r.pdf_url ? (
                        <a
                          href={r.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "28px",
                            height: "28px",
                            borderRadius: "6px",
                            background: "rgba(59,130,246,0.08)",
                            color: "#3b82f6",
                            border: "1px solid rgba(59,130,246,0.15)",
                            textDecoration: "none",
                          }}
                          title="View PDF"
                        >
                          <ExternalLink size={13} />
                        </a>
                      ) : (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "28px",
                            height: "28px",
                            borderRadius: "6px",
                            background: "var(--bg-tertiary)",
                            color: "var(--text-muted)",
                            opacity: 0.3,
                            border: "1px solid var(--border-color-light)",
                          }}
                        >
                          <FileText size={12} />
                        </span>
                      )}
                    </td>
                    {/* Edit */}
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      <button
                        onClick={() => setEditingReport(r)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "26px",
                          height: "26px",
                          borderRadius: "6px",
                          background: "transparent",
                          color: "var(--text-muted)",
                          border: "none",
                          cursor: "pointer",
                        }}
                        title="Edit claim"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "rgba(59,130,246,0.08)";
                          e.currentTarget.style.color = "#3b82f6";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color = "var(--text-muted)";
                        }}
                      >
                        <Pencil size={13} />
                      </button>
                    </td>
                    {/* Delete */}
                    <td style={{ padding: "8px 12px", textAlign: "center" }}>
                      <button
                        onClick={() => setDeleteConfirm(r.id)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "26px",
                          height: "26px",
                          borderRadius: "6px",
                          background: "transparent",
                          color: "var(--text-muted)",
                          border: "none",
                          cursor: "pointer",
                        }}
                        title="Delete"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "rgba(239,68,68,0.08)";
                          e.currentTarget.style.color = "#ef4444";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.color = "var(--text-muted)";
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => !deleting && setDeleteConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Claim Record</h3>
            </div>
            <div className="modal-body">
              <p style={{ color: "var(--text-secondary)" }}>
                Are you sure you want to delete this claim record? This will
                permanently remove the report, all version history, uploaded
                receipts, payment schedules, and generated PDFs.
              </p>
              <p style={{ color: "var(--color-error)", fontWeight: 600, marginTop: 8, fontSize: 13 }}>
                This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleDelete(deleteConfirm)}
                style={{ background: "var(--color-error)", opacity: deleting ? 0.7 : 1 }}
                disabled={deleting}
              >
                {deleting ? (
                  <><Loader2 size={16} className="spin" /> Deleting...</>
                ) : (
                  <><Trash2 size={16} /> Delete Record</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear Payment Confirmation */}
      {clearPaymentConfirm && (
        <div className="modal-overlay" onClick={() => !clearingPayment && setClearPaymentConfirm(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Clear Payment Data</h3>
            </div>
            <div className="modal-body">
              <p style={{ color: "var(--text-secondary)" }}>
                This will remove <strong>all payment data</strong> for Claim #{clearPaymentConfirm.claim_number} ({MONTH_SHORT[clearPaymentConfirm.claim_month - 1]}-{String(clearPaymentConfirm.claim_year).slice(-2)}), including progress payments, variation payments, and retention releases.
              </p>
              {deriveClaimsReportDisplayFinancials(clearPaymentConfirm).amountReceived > 0 && (
                <p data-seq-private="" style={{ color: "#d97706", fontWeight: 600, marginTop: 8, fontSize: 13 }}>
                  Currently recorded: {formatCurrency(deriveClaimsReportDisplayFinancials(clearPaymentConfirm).amountReceived)} paid
                </p>
              )}
              <p style={{ color: "var(--text-muted)", marginTop: 8, fontSize: 12 }}>
                The claim record itself will be kept, only payment data is removed.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setClearPaymentConfirm(null)}
                disabled={clearingPayment}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleClearPayment(clearPaymentConfirm)}
                style={{ background: "#dc2626", opacity: clearingPayment ? 0.7 : 1 }}
                disabled={clearingPayment}
              >
                {clearingPayment ? (
                  <><Loader2 size={16} className="spin" /> Clearing...</>
                ) : (
                  <><Trash2 size={16} /> Clear Payment</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {editingReport && (
        <ClaimEditModal
          report={editingReport}
          previousReport={
            reports[
            reports.findIndex((r) => r.id === editingReport.id) - 1
            ] || null
          }
          onClose={() => setEditingReport(null)}
          onSaved={() => {
            setEditingReport(null);
            fetchReports();
          }}
        />
      )}

      {/* Schedule Check Report Modal */}
      {viewingScheduleReport && viewingScheduleReport.schedule_check_result && (
        <ScheduleCheckReportModal
          result={viewingScheduleReport.schedule_check_result}
          claimNumber={viewingScheduleReport.claim_number}
          analyzedAt={viewingScheduleReport.schedule_check_at}
          paymentScheduleUrl={viewingScheduleReport.payment_schedule_url}
          reportId={viewingScheduleReport.id}
          checkStatus={viewingScheduleReport.schedule_check_status}
          onUpdate={(updatedResult) => {
            setReports(prev => prev.map(r => r.id === viewingScheduleReport.id ? { ...r, schedule_check_result: updatedResult } : r));
            setViewingScheduleReport(prev => prev ? { ...prev, schedule_check_result: updatedResult } : null);
          }}
          onClose={() => setViewingScheduleReport(null)}
          onRetry={() => {
            const report = viewingScheduleReport;
            setViewingScheduleReport(null);
            setScheduleUploadModalState({ reportId: report.id, file: null });
            setScheduleUploadInstructions("");
          }}
          onReupload={() => {
            const id = viewingScheduleReport.id;
            setViewingScheduleReport(null);
            setScheduleUploadId(id);
            scheduleRef.current?.click();
          }}
        />
      )}

      {/* Hidden file input for schedule upload */}
      <input
        ref={scheduleRef}
        type="file"
        accept="application/pdf,image/png,image/jpeg"
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && scheduleUploadId) {
            setScheduleUploadModalState({ reportId: scheduleUploadId, file });
            setScheduleUploadInstructions("");
          }
          e.target.value = "";
        }}
      />

      {/* Schedule AI Instruction Modal */}
      {scheduleUploadModalState && (() => {
        const windowCopy = instructionsWindowCopy(
          scheduleUploadModalState.file?.name,
          reports.find((r) => r.id === scheduleUploadModalState.reportId)?.payment_schedule_filename,
        );
        return (
        <div className="modal-overlay" onClick={() => setScheduleUploadModalState(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h3 className="modal-title">{windowCopy.title}</h3>
              <button
                className="modal-close"
                onClick={() => setScheduleUploadModalState(null)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '6px', fontSize: '0.85rem' }}>
                <strong>{windowCopy.sourceLabel}</strong> {windowCopy.sourceName}
              </div>

              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Sparkles size={14} color="#8b5cf6" />
                AI Guidance (Optional)
              </label>
              <textarea
                className="form-input"
                style={{ height: '100px', resize: 'vertical', fontSize: '0.85rem' }}
                placeholder="E.g., 'The retention total is in the top right corner of page 2' or 'Exclude the variations table from the total'."
                value={scheduleUploadInstructions}
                onChange={(e) => setScheduleUploadInstructions(e.target.value)}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '8px' }}>
                Provide hints to help the AI accurately read unusual or complex PDF structures.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setScheduleUploadModalState(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  const s = scheduleUploadModalState;
                  setScheduleUploadModalState(null);
                  if (s.file) {
                    handleScheduleUpload(s.reportId, s.file, scheduleUploadInstructions);
                    return;
                  }
                  const report = reports.find((r) => r.id === s.reportId);
                  if (report) handleScheduleRetry(report, scheduleUploadInstructions);
                }}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <ShieldCheck size={16} />
                {windowCopy.confirmLabel}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Retention Payment Modal */}
      {/* Paid by Builder Modal: Full Reconciliation Only */}
      {paidByBuilderReport && (
        <PaidByBuilderModal
          key={`pbb-${panelRefreshKey}`}
          report={paidByBuilderReport}
          variations={reportVariations}
          subcategoryClaims={reportSubcategoryClaims}
          existingLineItems={reportExistingLineItems}
          retentionAvailableAmount={getRetentionAvailableForReport(paidByBuilderReport)}
          onClose={() => {
            setPaidByBuilderReport(null);
            setReportVariations([]);
            setReportSubcategoryClaims([]);
            setReportExistingLineItems([]);
          }}
          onSaved={() => {
            setPaidByBuilderReport(null);
            setReportVariations([]);
            setReportSubcategoryClaims([]);
            setReportExistingLineItems([]);
            fetchReports();
            onVariationsChanged?.();
            onRetentionUpdate?.();
          }}
          onRefresh={async () => {
            // Re-fetch the report from DB to get updated claimed_set
            const { data: freshReport } = await supabase
              .from("claims_reports")
              .select("*")
              .eq("id", paidByBuilderReport.id)
              .single();
            const updatedReport = (freshReport as ClaimsReportRow) || paidByBuilderReport;
            await handleOpenPaidByBuilder(updatedReport);
            setPanelRefreshKey((k) => k + 1);
            fetchReports();
            onVariationsChanged?.();
            onRetentionUpdate?.();
          }}
        />
      )}

      {/* Standalone Progress Payment Panel */}
      {progressPanelData && (
        <ProgressPaymentPanel
          report={progressPanelData.report}
          subcategoryClaims={progressPanelData.subcategoryClaims}
          existingLineItems={progressPanelData.existingLineItems}
          onClose={() => setProgressPanelData(null)}
          onSaved={() => {
            setProgressPanelData(null);
            fetchReports();
          }}
        />
      )}

      {/* Standalone Variation Payment Panel */}
      {variationPanelData && (
        <VariationPaymentPanel
          key={`vpp-${panelRefreshKey}`}
          report={variationPanelData.report}
          variations={variationPanelData.variations}
          existingLineItems={variationPanelData.existingLineItems}
          onClose={() => setVariationPanelData(null)}
          onSaved={() => {
            setVariationPanelData(null);
            fetchReports();
            onVariationsChanged?.();
          }}
          onRefresh={async () => {
            // Re-fetch variation data and force re-mount
            const report = variationPanelData.report;
            const existingItems = await fetchPaymentLineItems(report.id);
            // Deliberately NOT filtered by dashpivot_removed_at: reconciliation of
            // an already-invoiced variation must survive its Dashpivot form
            // vanishing. The frozen claimed_set bounds this list.
            const { data: allProjectVariations } = await supabase
              .from("claims_variations")
              .select("*")
              .eq("project_id", report.project_id);
            const allVars = (allProjectVariations as ClaimsVariation[]) || [];

            // Re-fetch the report itself to get updated claimed_set
            const { data: freshReport } = await supabase
              .from("claims_reports")
              .select("*")
              .eq("id", report.id)
              .single();
            const updatedReport = (freshReport as ClaimsReportRow) || report;

            const frozenVars: ClaimedSetVariationSnapshot[] | null = Array.isArray(
              updatedReport.claimed_set?.variations
            ) ? updatedReport.claimed_set.variations : null;
            const realFrozenVars = (frozenVars ?? []).filter(isUsableFrozenVariation);

            let variationsData: ClaimsVariation[];
            if (frozenVars) {
              const byId = new Map(allVars.map((v) => [v.id, v]));
              variationsData = realFrozenVars
                .map((fv) => {
                  const match = byId.get(fv.variation_id);
                  if (!match) return null;
                  return { ...match, total: claimedSetVariationAmount(fv), discount_percent: 0 } as ClaimsVariation;
                })
                .filter((v): v is ClaimsVariation => v !== null);
            } else {
              const variationWaiting = (v: ClaimsVariation): number => {
                const total = Number(v.total ?? 0);
                const discount = Number(v.discount_percent ?? 0);
                const effective = discount > 0 ? total * (1 - discount / 100) : total;
                return Math.round((effective - Number(v.approved_amount ?? 0)) * 100) / 100;
              };
              const isInReportMonth = (v: ClaimsVariation): boolean => {
                const d = v.variation_date ? new Date(v.variation_date) : null;
                return !!d && d.getMonth() + 1 === report.claim_month && d.getFullYear() === report.claim_year;
              };
              const isPriorOutstanding = (v: ClaimsVariation): boolean => {
                const d = v.variation_date ? new Date(v.variation_date) : null;
                if (!d) return false;
                const before = d.getFullYear() < report.claim_year ||
                  (d.getFullYear() === report.claim_year && d.getMonth() + 1 < report.claim_month);
                return before && variationWaiting(v) > 0.01;
              };
              variationsData = allVars.filter((v) => isInReportMonth(v) || isPriorOutstanding(v));
            }

            setVariationPanelData({ report: updatedReport, variations: variationsData, existingLineItems: existingItems });
            setPanelRefreshKey((k) => k + 1);
            fetchReports();
            onVariationsChanged?.();
          }}
        />
      )}

      {retentionPanelData && (
        <RetentionPaymentPanel
          key={`rpp-${panelRefreshKey}`}
          report={retentionPanelData.report}
          existingLineItems={retentionPanelData.existingLineItems}
          retentionAvailableAmount={retentionPanelData.retentionAvailableAmount}
          onClose={() => setRetentionPanelData(null)}
          onSaved={() => {
            setRetentionPanelData(null);
            fetchReports();
            onRetentionUpdate?.();
          }}
        />
      )}

      {paymentHistoryReport && (
        <PaymentHistoryDrawer
          report={paymentHistoryReport}
          onClose={() => setPaymentHistoryReport(null)}
          onReverted={() => {
            setPaymentHistoryReport(null);
            fetchReports();
            onVariationsChanged?.();
            onRetentionUpdate?.();
          }}
        />
      )}

      {/* Payment Options Popover */}
      {paymentPopover && createPortal(
        <>
          {/* Backdrop */}
          <div
            style={{ position: "fixed", inset: 0, zIndex: 10020 }}
            onClick={() => setPaymentPopover(null)}
          />
          {/* Popover */}
          <div
            data-tour-id="cr-payment-popover"
            role="menu"
            aria-label="Select payment type"
            style={{
              position: "fixed",
              ...(paymentPopoverLayout || {}),
              background: "#fff",
              borderRadius: 14,
              border: "1px solid #e2e8f0",
              boxShadow: "0 12px 40px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.03)",
              zIndex: 10021,
              overflowX: "hidden",
              overflowY: "auto",
              animation: "popoverIn 0.15s ease-out",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "10px 14px 8px",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                Select Payment Type
              </div>
            </div>

            {/* Options */}
            <div style={{ padding: "4px 0" }}>
              {/* Progress */}
              <button
                type="button"
                data-tour-id="cr-option-progress"
                onClick={() => handlePaymentOptionSelect("progress")}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "10px 14px", border: "none", background: "transparent",
                  cursor: "pointer", textAlign: "left", transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f0fdf4"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #16a34a, #22c55e)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <TrendingUp size={15} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#0f172a" }}>Progress Payment</div>
                  <div style={{ fontSize: "0.62rem", color: "#64748b" }}>Enter amount paid from schedule</div>
                </div>
              </button>

              {/* Variations */}
              <button
                type="button"
                data-tour-id="cr-option-variations"
                onClick={() => handlePaymentOptionSelect("variations")}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "10px 14px", border: "none", background: "transparent",
                  cursor: "pointer", textAlign: "left", transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#f5f3ff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #7c3aed, #a78bfa)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Layers size={15} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#0f172a" }}>Variations</div>
                  <div style={{ fontSize: "0.62rem", color: "#64748b" }}>Manage variation payments & actions</div>
                </div>
              </button>

              {/* Retention */}
              <button
                type="button"
                data-tour-id="cr-option-retention"
                onClick={() => handlePaymentOptionSelect("retention")}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "10px 14px", border: "none", background: "transparent",
                  cursor: "pointer", textAlign: "left", transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#fffbeb"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #d97706, #f59e0b)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Lock size={15} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#0f172a" }}>Retention Release</div>
                  <div data-seq-private="" style={{ fontSize: "0.62rem", color: "#64748b" }}>
                    Outstanding balance {formatCurrency(getRetentionAvailableForReport(paymentPopover.report))}
                  </div>
                </div>
              </button>

            </div>

            {/* Footer: View All */}
            <div style={{ borderTop: "1px solid #f1f5f9", padding: "4px 0" }}>
              {paymentPopoverHasHistory && (
                <button
                  type="button"
                  onClick={() => {
                    const report = paymentPopover.report;
                    setPaymentPopover(null);
                    setPaymentHistoryReport(report);
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%",
                    padding: "9px 14px", border: "none", background: "transparent",
                    cursor: "pointer", textAlign: "left", transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #475569, #0f172a)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <History size={15} color="#fff" />
                  </div>
                  <div>
                    <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#0f172a" }}>Payment History</div>
                    <div style={{ fontSize: "0.62rem", color: "#64748b" }}>View versions and revert</div>
                  </div>
                </button>
              )}

              <button
                type="button"
                data-tour-id="cr-option-full"
                onClick={() => handlePaymentOptionSelect("all")}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "9px 14px", border: "none", background: "transparent",
                  cursor: "pointer", textAlign: "left", transition: "background 0.1s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#eff6ff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <DollarSign size={15} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#0f172a" }}>Full Reconciliation</div>
                  <div style={{ fontSize: "0.62rem", color: "#64748b" }}>View all sections at once</div>
                </div>
              </button>
            </div>

            {/* Clear Payment: only show when there IS payment data */}
            {paymentPopoverHasClear ? (
              <div style={{ borderTop: "1px solid #fee2e2", padding: "4px 0" }}>
                <button
                  type="button"
                  onClick={() => {
                    const report = paymentPopover.report;
                    setPaymentPopover(null);
                    setClearPaymentConfirm(report);
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%",
                    padding: "9px 14px", border: "none", background: "transparent",
                    cursor: "pointer", textAlign: "left", transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#fef2f2"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg, #dc2626, #ef4444)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Trash2 size={15} color="#fff" />
                  </div>
                  <div>
                    <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "#dc2626" }}>Clear Payment</div>
                    <div style={{ fontSize: "0.62rem", color: "#94a3b8" }}>Remove all payment data</div>
                  </div>
                </button>
              </div>
            ) : null}
          </div>
        </>,
        document.body
      )}
    </div>

      {/* ── Onboarding Tour ── */}
      {tour.activeTour && (
        <TourEngine
          tour={tour.activeTour}
          onComplete={tour.complete}
          onSkip={tour.skip}
        />
      )}

      {/* ── Confirm un-reconcile (prevents accidental clearing) ── */}
      <VaultConfirmModal
        isOpen={!!unreconcileTarget}
        variant="warning"
        title="Un-reconcile this claim?"
        message={
          <>
            This claim was reconciled by{" "}
            <strong>{unreconcileTarget?.name}</strong>
            {unreconcileTarget?.at ? (
              <>
                {" "}
                on <strong>{fmtDate(unreconcileTarget.at)}</strong>
              </>
            ) : null}
            .
            <br />
            <br />
            Un-reconciling clears its reconciled status, date, and the record of
            who reconciled it. You can mark it as reconciled again later.
          </>
        }
        confirmLabel="Un-reconcile"
        cancelLabel="Keep reconciled"
        onConfirm={() => {
          if (unreconcileTarget) handleReconcileClear(unreconcileTarget.id);
          setUnreconcileTarget(null);
        }}
        onCancel={() => setUnreconcileTarget(null)}
      />

      {/* ── Mark as reconciled (modern date picker) ── */}
      <ReconcileModal
        isOpen={reconcilingId !== null}
        value={reconciledDate}
        onChange={setReconciledDate}
        maxDate={localTodayStr()}
        reconcilerName={profile?.full_name || user?.email || "Unknown"}
        onConfirm={() => {
          if (reconcilingId) handleReconcileSave(reconcilingId);
        }}
        onCancel={() => {
          setReconcilingId(null);
          setReconciledDate("");
        }}
      />
    </>
  );
}
